import {
  createContext,
  forwardRef,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cerrarSesion } from '../../lib/cerrarSesion';
import { ChevronRight, Copy, Crop, Instagram, LayoutGrid, Maximize, Megaphone, MoveRight, Pencil, Play, type LucideIcon } from 'lucide-react';
import { useSidebarColapsable } from './useSidebarColapsable';
// ≥1024px: el ancho a partir del cual la sidebar se puede desplegar sin comerse el
// contenido (ADR-2026-08-05-el-shell-nuevo-arranca-en-768).
import { useIsDesktop } from '../../components/onboarding/useIsDesktop';
import { useBrandProfile, type BrandRow } from '../../hooks/useBrandProfile';
import { getUserPlan, planAllowsMultiBrand } from '../../lib/planUtils';
import { useShellGuards } from '../../hooks/useShellGuards';
import { esRutaShellLegacy } from '../../lib/rutasShellLegacy';
import { useCuentaAtlas } from '../../hooks/useCuentaAtlas';
import {
  useCreacionesRecientes,
  type CreacionReciente,
} from '../../hooks/useCreacionesRecientes';
import { GeneratedImagesHistory } from '../GeneratedImagesHistory';
import { SidebarContext, useSidebar } from '../../context/SidebarContext';
import { usePermissions } from '../../context/PermissionContext';
import { PanelChatAtlas, useEmpujeChat } from '../../chat-atlas';
import {
  ModalQRWhatsApp,
  urlsWhatsApp,
} from '../../components/ContactarPorWhatsApp';
import { CreditsTopBar } from '../../components/CreditsTopBar';
import { CreditsPricingSheet } from '../../components/pricing/CreditsPricingSheet';
import {
  copyPricingPorMotivo,
  type MotivoPricing,
} from '../../components/pricing/pricingContexts';
import { CAPACIDAD_POR_PLAN, estadoDeCreditos } from '../../lib/creditsStatus';
// De este módulo va quedando cada vez menos, y a propósito:
//  - `CREACIONES_RECIENTES` / `ItemGaleria` salieron con ADR-2026-07-31-shell-nuevo-datos-reales —
//    las creaciones son las del usuario (`useCreacionesRecientes`), no las imágenes
//    de demo del alta.
//  - `elegirFilasPlantillas` salió con ADR-2026-07-31-inicio-se-funde-interfaz-v2 — las
//    plantillas son las colecciones reales que trae `InterfazV2`.
// Sigue vivo `CARRUSEL_MODOS`, pero SÓLO como fuente de carátulas (video/póster):
// qué modos hay lo decide el llamador. `TODAS_LAS_HERRAMIENTAS` alimenta la sidebar,
// que sí es un menú fijo.
import {
  CARRUSEL_MODOS,
  TODAS_LAS_HERRAMIENTAS,
  saludo,
  type HerramientaAtlas,
} from './contenidoAtlas';
import {
  MODE_PATH_TO_KIND,
  modoVisiblePorFlags,
  anuncioTercero,
  portadaDeRubro,
} from '../v2/modesCatalog';
// `isPaidPlan` ya no se importa acá: las dos secciones que lo usaban —"Creaciones
// recientes" y su fila en el esqueleto— pasaron a `muestraCreacionesRecientes`, que
// además del plan mira el rubro (ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda).
import { getModePlanLock, type ModePlanLock } from '../../lib/planCapabilities';
import { rubroVePlantillas, esRubroHogarDeco } from '../../lib/rubro';
import { muestraCreacionesRecientes } from '../../lib/seccionesHome';
import { useRubroMarcaActiva } from '../../hooks/useRubroMarca';
import { Icon } from '../../design-system/icons';

/**
 * Shell de la plataforma en **desktop** — nacido como "Estilo 3 / Minimal"
 * (referencia: Vidnoz, ADR-2026-07-30-tres-estilos-interfaz-shells-propios) y promovido a interfaz real en
 * ADR-2026-07-31-shell-nuevo-urls-viejas + ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * Shell claro y vacío: sidebar flotante con lo mínimo, saludo de bienvenida, carrusel
 * de modos y el resto del espacio libre.
 *
 * DEJÓ DE SER UN PREVIEW. Ya no vive detrás de `import.meta.env.DEV` ni corre con
 * datos simulados: es el layout de las rutas que ya existían —`/interfaz-v2/*`,
 * `/mi-marca`, `/redes-sociales/imagenes`, `/editor-video`, `/configuracion`— más
 * `/inicio`, que es la única pantalla nueva. Las URLs no cambiaron; cambió el chrome
 * que las envuelve.
 *
 * **Sólo desktop.** En mobile esas mismas rutas siguen montando `DashboardLayout`
 * (Sidebar + MobileBottomNav): lo decide `ShellApp` en `App.tsx` por viewport, con el
 * mismo patrón que `AccountSetupResultsRoute`. Este árbol asume `h-screen` con una
 * sidebar de 244px en flujo, que abajo de 768px se come la pantalla.
 *
 * De lo simulado no queda nada del usuario: identidad, plan, créditos, capacidad,
 * descuento, marca activa y creaciones recientes salen de las mismas tablas que lee
 * el Sidebar de producción. Lo que sigue siendo estático es el CATÁLOGO de producto
 * (modos y plantillas de `contenidoAtlas`), que es contenido, no datos de cuenta.
 *
 * Viene en dos variantes de superficie (ADR-2026-07-30-minimal-gris-variante-superficie-prop), que es lo ÚNICO que cambia entre
 * ellas: mismo layout, mismo contenido, mismo copy. Se resuelve por prop y no
 * duplicando la pantalla porque este estilo se sigue iterando: un clon habría quedado
 * desfasado al primer cambio.
 */

/** Qué superficie es fondo y cuál es figura. Lo único que separa a las dos variantes. */
export type VarianteMinimal = 'claro' | 'gris';

/**
 * Un modo en la tira de la home — ADR-2026-07-31-inicio-se-funde-interfaz-v2.
 *
 * La home ya no arma esta lista: se la pasa `InterfazV2`, que es la que sabe qué
 * modos existen para este usuario (feature flags, rubro) y qué pasa al abrir uno
 * (gating por plan, guardado del scroll, sheet vs navegación). Acá sólo se dibuja.
 */
export interface ModoHome {
  /** `MODES.path` (`auto`, `modelo`, …). Es la clave de la carátula del catálogo. */
  path: string;
  /** Nombre a mostrar si el modo no tiene entrada en `COPY_TIRA`. */
  titulo: string;
  /** Imagen del preset, como respaldo cuando el catálogo no tiene carátula propia. */
  imagen: string;
  /** El plan del usuario no lo incluye: se dibuja el candado. */
  bloqueado: boolean;
  /** Qué hacer al clickearlo. Lo resuelve el llamador, con su gating. */
  onAbrir: () => void;
}

/**
 * Una fila de plantillas de la home. Una fila = una colección real de las que trae
 * el backend (`CollectionV2`), no una categoría del catálogo estático.
 */
export interface FilaPlantillasHome {
  id: string;
  titulo: string;
  bajada?: string;
  items: {
    id: string;
    src: string;
    titulo: string;
    onAbrir: () => void;
  }[];
  /** "Ver más" de la fila — lleva a la colección completa. */
  onVerMas: () => void;
}

interface TemaMinimal {
  /** Fondo de la pantalla. */
  fondo: string;
  /** Sidebar. */
  panel: string;
  /** Bloques de contenido: tarjetas de sección y celdas de imagen. */
  tarjeta: string;
  /** Un escalón por encima de `tarjeta`: chips de ícono y la tarjeta de cuenta. */
  hueco: string;
  /** Celda "Ver más": vacía y punteada, va un tono por debajo del resto. */
  vacio: string;
  /** Fondo del ítem de sidebar seleccionado. Sigue la misma inversión que el panel. */
  seleccion: string;
}

const TEMAS: Record<VarianteMinimal, TemaMinimal> = {
  // Original: lienzo gris, contenido en blanco.
  claro: {
    fondo: 'bg-[#F9F9F9]',
    panel: 'bg-white',
    tarjeta: 'bg-white',
    hueco: 'bg-[#F2F3F5]',
    vacio: 'bg-[#FAFAFB]',
    // Sobre panel blanco el seleccionado necesita color propio: el tinte de marca.
    seleccion: 'bg-[#EBEFFE]',
  },
  // Invertida: lienzo blanco, contenido en gris. Los grises son los mismos de la
  // variante clara, cambiados de rol — no una paleta nueva.
  gris: {
    fondo: 'bg-white',
    panel: 'bg-[#F1F2F4]',
    tarjeta: 'bg-[#F4F5F7]',
    hueco: 'bg-white',
    vacio: 'bg-white',
    // Sobre panel gris alcanza con el blanco: la pastilla resalta por contraste de
    // superficie, sin sumar un color más. El ícono y el texto siguen en azul de marca.
    seleccion: 'bg-white',
  },
};

/**
 * Identidad del usuario — **una sola fuente** (ADR-2026-07-31-avatar-pieza-sola-identidad-sola), ahora REAL
 * (ADR-2026-07-31-shell-nuevo-datos-reales, que supersede el origen del dato). El nombre completo
 * manda: de él salen el nombre del saludo (el primero) y la inicial del avatar, así
 * que cambiar el nombre en un solo lugar sigue actualizando los tres. Lo que cambió
 * es de dónde viene: `profiles.name` vía `useCuentaAtlas`, y ya no una constante.
 *
 * Los fallbacks NO son un nombre de ejemplo. Mientras carga —o si la fila no trae
 * nombre— el saludo va sin nombre y la inicial sale del mail: un "Hola, Maxi" a un
 * usuario que no se llama así es peor que un "Hola" a secas, y era exactamente lo
 * que pasaba con las constantes del preview.
 */

/** Nombre de pila para el saludo. Cadena vacía si todavía no hay nombre. */
function nombrePila(nombreCompleto: string | null): string {
  return nombreCompleto?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Inicial del avatar. Del nombre completo y en mayúscula, sin asumir que ya lo esté:
 * un `name` cargado como "maxi" tiene que seguir dando "M". Si no hay nombre cae al
 * mail, que siempre existe con sesión.
 */
function inicialDe(nombreCompleto: string | null, email: string | null): string {
  const base = nombreCompleto?.trim() || email?.trim() || '';
  return (base[0] ?? '?').toUpperCase();
}

/**
 * Avatar del usuario — ADR-2026-07-31-avatar-pieza-sola-identidad-sola. Tinte de marca de fondo y la inicial en el azul 500,
 * el mismo par que usan el ítem de sidebar seleccionado y el chip de marca.
 *
 * Es un componente y no una clase suelta porque aparece en cuatro lugares (saludo,
 * tarjeta de cuenta, sidebar colapsada y cabecera del menú) y tienen que cambiar
 * juntos: antes el del saludo era un degradé y los otros tres un círculo negro.
 *
 * La inicial la toma de `CuentaContext` y no de una prop: los cuatro avatares son la
 * misma persona, y pasarla por props obligaba a que cada uno de los cuatro llamadores
 * —dos de ellos en la sidebar, uno en el menú, uno en la home— la recibiera y la
 * reenviara.
 */
function AvatarUsuario({
  px = 36,
  className = '',
}: {
  px?: number;
  className?: string;
}) {
  return (
    <span
      style={{ width: px, height: px }}
      className={`grid shrink-0 place-items-center rounded-xl bg-[#F4F4F5] ring-1 ring-black/[0.08] ${className}`}
    >
      <span
        className={`material-symbols-rounded ${GRIS_SHELL}`}
        style={{ fontSize: Math.round(px * 0.58) }}
      >
        person
      </span>
    </span>
  );
}

/**
 * Fallback del selector de marca: **sólo** se ve si no hay sesión o la cuenta no
 * tiene marca ni empresa cargada. Con sesión, el selector muestra la marca activa
 * real y su rubro (ADR-2026-07-31-rubro-selector-sale-marca-real).
 *
 * El rubro es una de las categorías reales del alta y no un string inventado, así
 * el fallback tiene el largo que va a tener el dato de verdad.
 */
const MARCA_EJEMPLO = { nombre: 'Casa Aurora', rubro: 'Hogar y decoración' };

/* Acá vivían `PLAN_DEFAULT` / `CREDITOS_DEFAULT` / `DESCUENTO_DEFAULT` (plan gratis,
   0 créditos, 50% off). Eran el contexto fijo del preview, que corría sin sesión.
   Ahora los tres salen de `useCuentaAtlas` — ADR-2026-07-31-shell-nuevo-datos-reales. El descuento en
   particular NO tiene default: sin cupón vigente la barra no promete ninguno, que es
   el mismo criterio de fallo cerrado que `lib/hotSaleCoupon`. */
/** `company_profiles.plan` → etiqueta, igual que el Sidebar de la app real. */
const ETIQUETA_PLAN: Record<string, string> = {
  gratis: 'Plan Gratis',
  emprendimiento: 'Plan Básico',
  negocio: 'Plan Pro',
  enterprise1: 'Plan Ultra',
  enterprise2: 'Plan Ultra',
  enterprise3: 'Plan Ultra',
};

/* La capacidad por plan —el denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app)— vive en
   `lib/creditsStatus` desde ADR-2026-07-31-contador-sidebar-habla-creditos-sale: estaba copiada acá y en `useHomeBanners`. */

/**
 * Nav de la sidebar. Apunta a las rutas CANÓNICAS de la plataforma
 * (ADR-2026-07-31-shell-nuevo-urls-viejas, que supersede ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo en el destino).
 *
 * Con el shell nuevo aplicado sobre las rutas que ya existían, `/creaciones` y
 * `/editar-marca` dejaron de ser pantallas propias: son alias que redirigen a
 * `/redes-sociales/imagenes` y `/mi-marca`. Si la sidebar siguiera apuntando al
 * alias, cada click haría un redirect visible y —peor— el ítem nunca quedaría
 * marcado, porque la URL final no coincide con la del ítem.
 *
 * "Inicio" apunta a `/interfaz-v2` desde ADR-2026-07-31-inicio-se-funde-interfaz-v2:
 * `/inicio` dejó de ser una pantalla y pasó a ser un alias más. La home real —con el
 * diseño nuevo y toda la lógica— es `/interfaz-v2`.
 */
const NAV_LATERAL = [
  // "Inicio" NO se gatea por `showsV2Studio` aunque el ítem equivalente de la sidebar
  // vieja ("Interfaz V2") sí lo estuviera — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
  // En desktop `HomeRedirect` manda a TODOS a `/interfaz-v2`: este shell ES la
  // experiencia V2, no una de dos opciones. Gatearlo por grupo dejaría a un Grupo A
  // parado justo en la pantalla que la sidebar se niega a listar.
  { label: 'Inicio', icono: 'home', ruta: '/interfaz-v2' },
  {
    label: 'Creaciones',
    icono: 'photo_library',
    ruta: '/redes-sociales/imagenes',
    permiso: 'imagenes',
  },
  // Sin `permiso`: en la sidebar vieja "Mi marca" es el único `alwaysShow` — no es
  // una feature gateada.
  { label: 'Marca', icono: 'shopping_bag', ruta: '/mi-marca' },
] as const satisfies readonly {
  label: string;
  icono: string;
  ruta: string;
  permiso?: string;
}[];

/**
 * Los accesos que tenía la sidebar VIEJA y a ésta le faltaban —
 * ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
 *
 * Son los nueve ítems de `FLAT_MENU_ITEMS` que no tienen equivalente acá: el Estudio
 * de marketing entero, los dos de "Empleado IA" y Test V1. Sin ellos, borrar la
 * sidebar vieja se llevaba puesto el único acceso a esas pantallas.
 *
 * **La lista entera es `devOnly`**, igual que en la sidebar vieja, así que no hace
 * falta un campo por ítem: se filtra de una (`permiso` + `dev_tester`). Un cliente no
 * ve nada de esto.
 *
 * NO está "Estudio Virtual" (el flujo clásico, `/redes-sociales/crear-anuncio`), que
 * en la vieja sí está y encima lo ve casi todo el mundo (`showsClassicStudio` da
 * `true` para Grupo A **y para grupo desconocido**, que es la mayoría de las cuentas
 * reales). Se deja afuera a propósito: esta sidebar nunca lo listó, en desktop
 * `HomeRedirect` ya manda a todos a `/interfaz-v2`, y agregarlo ahora sería
 * reintroducir el flujo clásico en escritorio para casi todos los clientes — un
 * cambio de producto, no la paridad de accesos que se estaba buscando.
 */
type AccesoInterno = {
  label: string;
  icono: string;
  ruta: string;
  /** La MISMA clave de `PermissionContext` que usa el `permissionKey` de la vieja. */
  permiso: string;
  /** Rótulo del grupo que abre este ítem (se dibuja arriba de él). */
  abreGrupo?: string;
  /**
   * Rutas extra que tienen que dejarlo marcado. Para los ítems que son la puerta de
   * una pantalla con pestañas, donde cada pestaña es su propia URL: sin esto el ítem
   * se apaga al cambiar de pestaña. La ruta propia no se repite acá.
   */
  rutasActivas?: string[];
};

const ACCESOS_INTERNOS: AccesoInterno[] = [
  { label: 'Mapa', icono: 'hub', ruta: '/estudio-marketing/mapa', permiso: 'em-mapa', abreGrupo: 'Estudio de marketing' },
  { label: 'Plan', icono: 'checklist', ruta: '/estudio-marketing/plan', permiso: 'em-plan' },
  {
    // Insights y Estrategia se fusionaron en una pantalla de dos pestañas, pero cada
    // pestaña sigue siendo su URL de siempre (ADR-2026-08-17-analisis-datos-tabs-por-ruta).
    label: 'Análisis de datos',
    icono: 'insights',
    ruta: '/estudio-marketing/insights',
    permiso: 'em-insights',
    rutasActivas: ['/estudio-marketing/estrategia'],
  },
  { label: 'Marca', icono: 'palette', ruta: '/estudio-marketing/marca', permiso: 'em-marca' },
  { label: 'Datos', icono: 'database', ruta: '/estudio-marketing/datos', permiso: 'em-datos' },
  { label: 'Puesta en marcha', icono: 'rocket_launch', ruta: '/estudio-marketing/onboarding', permiso: 'em-onboarding' },
  { label: 'Chat', icono: 'forum', ruta: '/chat', permiso: 'chat-atlas', abreGrupo: 'Empleado IA' },
  // OJO: `/redes-sociales/calendario` y `/test-v1` quedaron FUERA de la mudanza al
  // shell (ADR-2026-08-20-la-sidebar-vieja-la-elegia-el-grupo-de-rutas: se migró
  // A + B + E, no D), así que estos dos ítems TODAVÍA saltan al chrome viejo. Se
  // listan igual porque son `devOnly` —sólo los ve el equipo— y sin ellos no hay
  // ninguna otra puerta a esas pantallas desde escritorio.
  { label: 'Manager de Redes IA', icono: 'smart_toy', ruta: '/redes-sociales/calendario', permiso: 'calendario' },
  { label: 'Test V1', icono: 'science', ruta: '/test-v1', permiso: 'test-v1', abreGrupo: 'Interno' },
];

/**
 * Salida del fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja, afinado en ADR-2026-07-31-ajustes-shell-padding-secciones-hospedadas). El fade es
 * **asimétrico** a propósito: la salida es corta —lo que ya no sirve tiene que
 * irse rápido, si no el click se siente trabado— y la entrada larga (680ms, en la
 * clase del wrapper) para que la sección nueva aparezca de a poco.
 *
 * Duplicado respecto de los valores originales (120/340) a pedido: el fade tenía
 * que sentirse más sutil todavía.
 *
 * Sólo la salida es una constante: la entrada la aplica Tailwind y sus clases
 * tienen que ser literales para que el JIT las genere, así que interpolarla acá
 * no serviría.
 */
const FADE_SALIDA_MS = 240;

/**
 * Estado seleccionado de un ítem de sidebar: ícono y texto en el azul 500 de la marca
 * (`brand.500` / `--brand-primary`, #1644EB). El fondo lo pone el tema (`seleccion`),
 * que cambia con la variante.
 */
const ITEM_ACTIVO = 'text-brand-primary';

/**
 * El gris del shell: negro al 60%. Lo usan los ítems NO seleccionados de las dos
 * listas de la sidebar (ícono y etiqueta), el "Centro de ayuda" del pie, los
 * controles de arriba a la derecha y los "Ver todo" de las secciones.
 *
 * Antes cada uno tenía el suyo —70% el texto de la nav, 65% el pie y la pastilla de
 * ayuda, 55% el ícono del avatar— y encima los íconos de la nav sumaban un
 * `opacity-75` propio, así que terminaban al 52,5%: seis grises apenas distintos
 * que se leían como un desajuste y no como jerarquía. Un solo valor.
 */
const GRIS_SHELL = 'text-black/60';

/**
 * El mismo gris, más claro, para los ítems de la sidebar que el plan no habilita —
 * ADR-2026-08-03-items-bloqueados-mas-claros.
 *
 * El candado dice QUÉ pasa pero no baja el peso del ítem: en la lista los bloqueados
 * se leían igual de disponibles que los demás. Bajarle el gris los manda atrás sin
 * sacarlos, que es lo que se quiere — siguen siendo clickeables (abren el catálogo de
 * planes), así que no pueden desaparecer.
 *
 * Va sobre el COLOR y no como `opacity-40` en el botón: la opacidad se multiplicaría
 * con el 60% que ya tiene el texto y lo dejaría al 24%, demasiado tenue para algo que
 * igual se puede tocar.
 */
const GRIS_BLOQUEADO = 'text-black/40';

/**
 * "Ver todo" de las secciones de la home. Volvió a ser texto —sin caja, sin borde—
 * pero en bold: pegado al título (16px) ya se lee como parte de esa fila, así que la
 * caja lo único que hacía era competir con el título en peso visual. El bold es lo
 * que lo sostiene como control.
 *
 * Sin padding horizontal: con la caja, el `px-3` era parte del botón; sin ella,
 * separaba el texto del título más que los 16px que pide el layout.
 */
const BOTON_VER_TODO =
  'mb-1 flex shrink-0 items-center gap-1.5 text-[13px] font-bold text-brand-primary transition-colors hover:text-brand-primary-hover';

/* Acá vivía `MODO_APAGADO`, la tabla de modos escondidos por feature flag
   (ADR-2026-07-31-sidebar-filtra-modos-apagados). Tenía una sola entrada,
   `catalogo-muebles`, y el flag que la alimentaba (`CATALOGO_MUEBLES_ENABLED`)
   se eliminó al mergear `principal`: ese modo y su hermana Infografía ya no se
   apagan globalmente sino que dependen del RUBRO de la marca activa
   (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).

   Los dos se filtran ahora abajo, en `herramientasOrdenadas`, junto a Plantillas —
   los tres ítems de la sidebar que dependen del rubro y no de una constante de
   módulo. */

/**
 * Herramientas de la sidebar, con **Anuncio tercero**
 * (ADR-2026-08-03-anuncio-tercero-tira-y-sidebar): en el orden de `MODES` cae último,
 * detrás de los dos de catálogo y del asistente, y es de las cosas que más se usan.
 * La lista y la tira comparten la posición, así que el modo está en el mismo lugar se
 * lo busque donde se lo busque.
 *
 * Acá NO está el lugar de "Plantillas": ese depende del plan, así que lo resuelve
 * `ordenarHerramientas` más abajo. Se reordena en este módulo y no en
 * `TODAS_LAS_HERRAMIENTAS` para no mover la fila de íconos del estilo Workspace, que
 * la consume por índice.
 */
const HERRAMIENTAS_SIDEBAR = anuncioTercero(TODAS_LAS_HERRAMIENTAS, (h) => h.id);

/**
 * Orden final de la sidebar — ADR-2026-08-03-plantillas-cierra-los-disponibles.
 *
 * Tres bloques: los modos que el plan habilita, **Plantillas cerrando ese bloque**, y
 * los bloqueados al final.
 *
 * "Plantillas" no es un modo de generación: es un atajo al catálogo. Por eso venía
 * último de la lista entera — pero con los bloqueados abajo, "último" pasó a
 * significar "después de lo que no podés usar", y quedaba separada de las
 * herramientas con las que sí se trabaja. Cierra lo usable, que es de lo que forma
 * parte.
 *
 * Los bloqueados se mandan al fondo conservando su orden relativo: lo que el plan no
 * incluye es una oferta, no navegación, y no tiene por qué partir la lista al medio.
 */
function ordenarHerramientas(
  herramientas: HerramientaAtlas[],
  estaBloqueada: (h: HerramientaAtlas) => boolean,
): HerramientaAtlas[] {
  const disponibles = herramientas.filter((h) => h.id !== 'plantillas' && !estaBloqueada(h));
  const plantillas = herramientas.filter((h) => h.id === 'plantillas');
  const bloqueadas = herramientas.filter((h) => h.id !== 'plantillas' && estaBloqueada(h));
  return [...disponibles, ...plantillas, ...bloqueadas];
}

/**
 * Destino de un ítem de herramientas — ADR-2026-07-31-shell-nuevo-urls-viejas.
 *
 * Acá vivía `RUTA_EN_SHELL` + `RUTA_MODO_VIEJA`, la tabla que traducía cada ruta de
 * `contenidoAtlas` (`/interfaz-v2/modo/:mode`, `/interfaz-v2/plantillas`,
 * `/editor-video`) a un clon hospedado (`/modo/:mode`, `/plantillas`,
 * `/estudio-video`). Esa traducción existía porque las rutas originales montaban
 * `DashboardLayout` con su propia sidebar y había que esquivarlas.
 *
 * Ya no hace falta: son esas MISMAS rutas las que ahora se montan dentro de este
 * shell, así que el destino correcto es el que trae el catálogo y la función queda
 * en la identidad. Se deja como función —y no se reemplazan los llamados por
 * `h.ruta`— porque es el único punto donde habría que volver a intervenir si
 * alguna herramienta necesitara un destino propio en este shell.
 */
function rutaSidebar(h: { id: string; ruta: string }): string {
  return h.ruta;
}

/**
 * El historial, en su ruta canónica — ADR-2026-07-31-shell-nuevo-urls-viejas, supersede
 * ADR-2026-07-31-fail-closed-real-catalogo-enlaces.
 *
 * Ese ADR apuntó los enlaces de la home a `/creaciones` porque `/redes-sociales/imagenes`
 * vivía sólo bajo `DashboardLayout` y sacaba al usuario del shell nuevo a mitad de
 * flujo. Ahora `/redes-sociales/imagenes` ES la ruta hospedada, y `/creaciones` el
 * alias que redirige a ella: apuntar al alias sólo agregaría un salto.
 */
const RUTA_HISTORIAL = '/redes-sociales/imagenes';

/**
 * El perfil, por el mismo motivo que `RUTA_HISTORIAL`: lo enlazan la tarjeta de
 * cuenta y el menú del avatar, y los dos tienen que coincidir con lo que marca la
 * sidebar.
 *
 * `RUTA_PLANTILLAS` vivía acá y se fue con ADR-2026-07-31-inicio-se-funde-interfaz-v2:
 * el "Ver todo" de plantillas ahora lo resuelve el llamador, que es el mismo que
 * decide qué filas se muestran.
 */
const RUTA_PERFIL = '/configuracion';

/**
 * Ancho de la sidebar flotante, y el margen que la separa del borde y del contenido
 * (`m-2` = 8px). Se nombran porque además del `<aside>` los consume el override de
 * `SidebarContext` (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell): las pantallas hospedadas anclan paneles `fixed` a
 * `sidebarWidth`, y ese número tiene que ser el borde DERECHO de esta sidebar
 * —margen + ancho + margen— y no el ancho a secas.
 */
const ANCHO_SIDEBAR = 244;
const ANCHO_SIDEBAR_COLAPSADA = 66;
const MARGEN_SIDEBAR = 8;

/* El motivo por el que se abre el catálogo (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo) y su copy viven en
   `pricingContexts` desde ADR-2026-07-31-copy-catalogo-sigue-saldo-real: la tabla estaba duplicada acá y en
   `SubscriptionLimitModal`, y agregar un motivo arreglaba uno solo. */

/**
 * Abre el catálogo como overlay. Lo provee el shell para que lo consuma cualquier
 * descendiente, incluidas las secciones montadas en el `<Outlet />`: el estado del
 * overlay tiene que ser del shell, porque si viviera en la sección cambiar de
 * pantalla lo cerraría.
 *
 * Lleva además el **estado de cuenta** (plan y créditos) porque lo consume la misma
 * gente que la acción: el encabezado de la home muestra los créditos al lado del CTA
 * de mejora (ADR-2026-07-31-creditos-mejora-plan-encabezado-home), y esos números viven
 * en el shell. Pasarlos por props obligaría a que cada sección hospedada los reciba y
 * los reenvíe.
 *
 * Los defaults son inertes para que un consumidor montado fuera del shell no explote.
 */
interface CuentaCtx {
  abrirPricing: (motivo?: MotivoPricing) => void;
  creditos: number;
  planLabel: string;
  /**
   * `true` cuando no hay nada que resolver: plan pago **y** saldo por encima del
   * umbral. Es el criterio que decide si el CTA de mejora va sólido o en tinte
   * (ADR-2026-07-31-columna-contenido-90-mejorar-plan); se calcula una vez en el shell para que los dos botones —el de la
   * sidebar y el del encabezado— no puedan discrepar.
   */
  planAlDia: boolean;
  /* Identidad real del usuario — ADR-2026-07-31-shell-nuevo-datos-reales. Viaja por el contexto
     y no por props por lo mismo que el plan: la consumen los cuatro avatares, la
     cabecera del menú de cuenta y el saludo de la home, repartidos entre el shell
     y la sección. */
  /** `profiles.name`, o `null` mientras carga / si la fila no lo trae. */
  nombreCompleto: string | null;
  /** `profiles.email`, con el de la sesión como respaldo. */
  email: string | null;
  /** Inicial del avatar, ya derivada y en mayúscula (ver `inicialDe`). */
  inicial: string;
  /** Empresa del usuario — la home la necesita para sus creaciones recientes. */
  idCompany: string | null;
  /**
   * Marca activa (`brand_profiles.id`) — ADR-2026-08-03-creaciones-recientes-por-marca.
   * La consumen las secciones que muestran contenido generado, que es contenido DE
   * una marca y no de la cuenta. `null` mientras resuelve.
   */
  idMarca: string | null;
  /**
   * Rubro de la marca activa (`brand_profiles.rubro`) —
   * ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
   *
   * Va en el contexto y no como prop porque quien lo necesita es la TIRA DE MODOS, tres
   * niveles abajo, y sólo para elegir una carátula: enhebrarlo por props obligaba a
   * `HomeMinimal` —que no decide nada del rubro— a recibirlo y pasarlo. El shell ya lo
   * tiene resuelto para filtrar la sidebar, así que es el mismo valor y no una segunda
   * fuente. `null` mientras carga.
   */
  rubroMarca: string | null;
  /**
   * `true` sólo cuando el valor lo provee ESTE shell — ADR-2026-07-31-shell-provee-flag-hospedaje.
   *
   * Lo consultan las pantallas hospedadas que traen su propio chrome duplicado: la
   * barra de créditos y el panel CONTEXTO DEV los pinta el shell, así que
   * `InterfazV2` tiene que callarse los suyos o quedan dos.
   *
   * Es un flag de contexto y no una comparación de `pathname` ni un `useIsDesktop()`
   * a propósito: la pregunta real es "¿quién me montó?", y las dos aproximaciones se
   * desincronizan del router en cuanto se mueve una ruta. Justamente eso pasó con la
   * versión anterior (`pathname.startsWith('/modo/')`), que quedó siempre `false` al
   * pasar el shell a las URLs viejas (ADR-2026-07-31-shell-nuevo-urls-viejas).
   */
  dentroDelShell: boolean;
  /**
   * Corre `accion` con el MISMO fade de salida que usa la sidebar para cambiar de
   * sección (ADR-2026-08-03-tira-modos-usa-fade-del-shell): apaga el contenido,
   * espera `FADE_SALIDA_MS` y recién ahí ejecuta.
   *
   * Existe porque la tira de modos de la home no navega por su cuenta: el destino lo
   * arma `InterfazV2` (gating de plan → visor de ejemplos, guardado del scroll), así
   * que el shell no puede interceptar una ruta. Lo que se comparte es la TRANSICIÓN,
   * no el destino.
   *
   * Fuera del shell es identidad: se ejecuta en el acto, sin fade.
   */
  conFadeDeSalida: (accion: () => void) => void;
  /**
   * `true` mientras `useCuentaAtlas` no resolvió — ADR-2026-08-03-home-aparece-entera-o-skeleton.
   *
   * Todo lo demás de este contexto ya tiene un valor mientras carga (0 créditos,
   * nombre `null`), que es lo correcto para la sidebar —muestra el hueco y se
   * completa— pero no para la home, que necesita saber si esperar.
   */
  cargandoCuenta: boolean;
}

const CuentaContext = createContext<CuentaCtx>({
  abrirPricing: () => {},
  creditos: 0,
  planLabel: '',
  planAlDia: false,
  nombreCompleto: null,
  email: null,
  inicial: '?',
  idCompany: null,
  idMarca: null,
  rubroMarca: null,
  // Default `false`: un consumidor montado fuera del shell —InterfazV2 en mobile,
  // bajo `DashboardLayout`— tiene que seguir pintando su propio chrome.
  dentroDelShell: false,
  // Sin shell no hay nada que apagar: la acción corre tal cual.
  conFadeDeSalida: (accion) => accion(),
  // Sin shell no hay cuenta que esperar: el consumidor dibuja con lo que tenga.
  cargandoCuenta: false,
});

/**
 * Estado de cuenta y hospedaje, para las pantallas montadas en el `<Outlet />`.
 * Se exporta el hook y no el contexto: los consumidores no tienen por qué saber que
 * esto es un `createContext`, y así el default inerte no se puede saltear.
 */
export function useCuentaShell(): CuentaCtx {
  return useContext(CuentaContext);
}

/**
 * Clases del CTA "Mejorar plan" según ese criterio. Sólido cuando hay algo que
 * resolver; en tinte azul con texto de marca cuando no —a un cliente pago con
 * créditos de sobra el botón no le está pidiendo nada—.
 */
function clasesMejorarPlan(planAlDia: boolean): string {
  return planAlDia
    ? 'bg-[#EBEFFE] text-brand-primary hover:bg-[#DFE6FD]'
    : 'bg-brand-primary text-white hover:bg-brand-primary-hover';
}

/**
 * Mensaje que precarga el link directo del modal de soporte —
 * ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio.
 *
 * Acá vivía `WHATSAPP_URL = 'https://wa.me/5491122635392'`, la TERCERA copia del número
 * (las otras dos: `components/ContactarPorWhatsApp.tsx` y `pages/Ayuda.tsx`). Se fue: el
 * número lo arma ahora `urlsWhatsApp()`, en un solo lugar.
 *
 * Genérico a propósito, aunque este mensaje SÍ podría llevar datos del cliente (el link
 * directo va derecho a WhatsApp, sin intermediarios): el botón es "hablá con soporte"
 * sin ningún contexto de qué le pasa al usuario, así que no hay nada que precargar.
 */
const MENSAJE_SOPORTE = 'Buenas, me contactaba por mi cuenta de Atlas.';

/**
 * "Ediciones rápidas": las acciones que hoy aparecen al abrir una imagen recién
 * generada (la barra de `GeneratedImagesInline`). Mismos íconos —los lucide que usa
 * esa barra— y mismos títulos; la descripción se agrega acá, que en la barra no entra.
 *
 * Las rutas apuntan a donde vive de verdad cada acción: las que necesitan una imagen
 * abierta llevan al historial, que es donde se abre.
 */
export const EDICIONES_RAPIDAS = [
  {
    label: 'Editar',
    desc: 'Cambiá lo que quieras con un prompt',
    Icono: Pencil,
    // A la pantalla de atajos, que resuelve justamente lo que este comentario decía
    // que faltaba — ADR-2026-08-20-los-atajos-tienen-pantalla-propia-para-elegir-imagen.
    // Acá decía: "`/redes-sociales/editar-imagen` necesita una imagen abierta que desde
    // acá no existe, así que se va al historial". Ahora el paso de elegir la imagen
    // existe, y una vez elegida sí se puede ir derecho a esa pantalla (que además ya
    // vive en el shell nuevo).
    ruta: '/atajo/editar',
  },
  // Carrusel y Animar SÍ tienen pantalla propia, y ahora el atajo lleva ahí —
  // ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
  //
  // Iban al historial como el resto porque cuando se escribió esta lista eran botones
  // escondidos adentro del visor. Desde ADR-2026-08-14 son modos con pantalla propia
  // que ARRANCAN justo en el paso de elegir imagen (subida o generada), que es
  // exactamente lo que el atajo quiere: mandar al historial era un rodeo que además
  // dejaba al usuario adivinando qué hacer al llegar.
  {
    label: 'Carrusel',
    desc: 'Más tomas del mismo producto',
    Icono: Copy,
    ruta: '/carrusel',
  },
  {
    label: 'Animar',
    desc: 'Convertí la foto en video',
    Icono: Play,
    ruta: '/animar-imagen',
    // Pro/Ultra, igual que su card en la tira y su ítem en la sidebar
    // (`getModePlanLock('animar-imagen', plan)`). Sin esto el atajo era la única puerta
    // al modo SIN candado: se entraba, se elegía la imagen, se escribía el prompt, y el
    // bloqueo aparecía recién al generar (`AnimarImagenMode` sólo frena ahí).
    kindParaCandado: 'animar-imagen',
  },
  {
    label: 'Anuncio',
    desc: 'Sumale texto y formato de aviso',
    Icono: Megaphone,
    ruta: '/interfaz-v2/modo/anuncio',
  },
  {
    label: 'Descripción',
    desc: 'El texto listo para el post',
    Icono: Instagram,
    ruta: '/atajo/descripcion',
  },
  { label: 'Recortar', desc: 'Ajustá el encuadre', Icono: Crop, ruta: '/atajo/recortar' },
  {
    label: 'Mejorar',
    desc: 'Subí la resolución con IA',
    Icono: Maximize,
    ruta: '/atajo/mejorar',
  },
] satisfies readonly {
  label: string;
  desc: string;
  /** El componente de ícono, tal cual lo exporta lucide. Se tipa `LucideIcon` y no un
   *  `ComponentType<{className}>` genérico: lucide devuelve `ForwardRefExoticComponent`,
   *  que NO es asignable a `ComponentType`, y con el tipo genérico el `satisfies`
   *  quedaba en error — o sea inerte, sin validar nada. */
  Icono: LucideIcon;
  ruta: string;
  /**
   * `kind` de `MODES` con el que se pide el candado por plan. Sólo lo declaran los
   * atajos que son la puerta de un modo gateado — hoy Animar.
   */
  kindParaCandado?: string;
}[];

/**
 * El candado de un atajo, o `null` — ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
 *
 * Existe para que los DOS consumidores de `EDICIONES_RAPIDAS` —la sección de la home
 * (`SeccionesHome`) y el sheet de Generar de mobile (`useModosGenerar`)— resuelvan el
 * bloqueo con la MISMA regla. Están en archivos distintos y ninguno ve al otro: sin un
 * punto común, el mismo atajo terminaba abriendo en una superficie y frenando en la
 * otra.
 */
export function candadoEdicionRapida(
  edicion: { kindParaCandado?: string },
  plan: string | null | undefined,
): ModePlanLock | null {
  return edicion.kindParaCandado
    ? getModePlanLock(edicion.kindParaCandado, plan)
    : null;
}

/**
 * Cada cuánto pasa solo de un modo al siguiente. Es también el techo de reproducción
 * de las carátulas en video (ver `CardModo`): duran ~6 s y se cortan acá, así ninguna
 * sigue corriendo después de que la tira ya avanzó.
 */
/**
 * Miniaturas de creaciones y plantillas: en MOBILE una sola fila con scroll
 * horizontal; de `sm` para arriba, la grilla de siempre
 * (ADR-2026-07-31-home-mobile-filas-horizontales-ediciones-compactas).
 *
 * En un teléfono la grilla de dos columnas obligaba a scrollear media pantalla por
 * sección para pasar de largo seis miniaturas; en fila, cada sección ocupa el alto
 * de UNA miniatura y el resto se recorre de costado, que es lo que ya hace el
 * usuario en el resto de la app.
 *
 * El `-mx-4 px-4` la lleva a sangre: la fila arranca alineada con el título pero
 * sigue hasta el borde de la TARJETA, así se ve que hay más a la derecha. Se anula
 * en `sm:` porque ahí el contenedor ya no tiene ese padding.
 *
 * Los 4 (16px) son exactamente el `p-4` de la tarjeta que la contiene, y de eso
 * depende que las miniaturas no se salgan: el margen negativo define hasta dónde
 * llega la caja que RECORTA el scroll horizontal. Con `-mx-5` (20px) esa caja
 * quedaba 4px más ancha que la tarjeta de cada lado y, al scrollear, las imágenes se
 * dibujaban afuera del borde redondeado (ADR-2026-08-04-la-fila-se-recorta-en-la-tarjeta).
 */
const FILA_MINIATURAS =
  'scrollbar-hide -mx-4 flex gap-3 overflow-x-auto px-4 sm:mx-0 sm:grid sm:gap-4 sm:overflow-visible sm:px-0 sm:grid-cols-3 xl:grid-cols-6';

/** Ancho fijo de cada miniatura mientras es una fila; en grilla lo da la columna. */
const CELDA_MINIATURA = 'w-[132px] shrink-0 sm:w-auto';

/**
 * Fila de "Creaciones recientes" — ADR-2026-08-03-creaciones-recientes-respetan-su-forma.
 *
 * A diferencia de `FILA_MINIATURAS` (que es una GRILLA de columnas iguales, la que
 * usan las plantillas), acá cada miniatura mide distinto: el alto es el mismo para
 * todas y el ancho lo pone la proporción de cada imagen. Con columnas iguales eso no
 * se puede expresar, así que es una fila flex.
 *
 * En DESKTOP no scrollea: se muestran las que entran y "Ver más" se estira para
 * llenar lo que sobra (ADR-2026-08-03-creaciones-entran-las-que-entran). En mobile sí,
 * que es el patrón esperado ahí y donde a lo sumo entraría una miniatura.
 */
const FILA_CREACIONES =
  'scrollbar-hide -mx-5 flex gap-3 overflow-x-auto px-5 sm:-mx-8 sm:gap-4 sm:overflow-x-visible sm:px-8';

/**
 * Alto de las miniaturas de Creaciones. Es el ÚNICO valor fijo de la celda: el ancho
 * sale de multiplicarlo por la proporción de cada imagen.
 *
 * Los 176px de mobile son exactamente lo que medían antes (132px de ancho por la
 * proporción 3:4 con la que se recortaban), así que esa fila no cambia de alto.
 *
 * ⚠️ `ALTO_CREACION_PX` tiene que seguir a la clase de `sm:`: el cálculo de cuántas
 * miniaturas entran multiplica ESE número por cada proporción. Si se cambia una y no
 * la otra, la cuenta da distinto de lo que se dibuja.
 */
const ALTO_CREACION = 'h-[176px] sm:h-[300px]';
const ALTO_CREACION_PX = 300;

/** Idem: tiene que seguir al `sm:gap-4` de `FILA_CREACIONES`. */
const GAP_CREACION_PX = 16;

/**
 * Ancho mínimo de la celda "Ver más". Se estira para ocupar lo que sobre, pero nunca
 * baja de acá: es lo que se le reserva al calcular cuántas miniaturas entran, y por
 * debajo el círculo y la etiqueta se apretarían.
 */
const MIN_VER_MAS_PX = 132;

/**
 * Proporción con la que se dibuja una miniatura mientras no se sabe la suya: es el
 * formato más común de la base (4:5, seis de cada diez). Se usa en los esqueletos de
 * carga y en las filas sin `formato`, hasta que la imagen carga y se mide sola.
 */
const RATIO_CREACION_DEFECTO = 4 / 5;

/**
 * Cuántas miniaturas entran en `ancho` sin desbordar, dejándole a "Ver más" su mínimo
 * — ADR-2026-08-03-creaciones-entran-las-que-entran.
 *
 * Se recorren en orden y se van sumando: cada una ocupa su ancho (alto × proporción)
 * más el gap que la separa de la anterior. La primera que no entra corta la lista.
 *
 * `reserva` es el lugar que hay que dejarle a "Ver más". Se pregunta DOS veces
 * (ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas): primero con `0`, para saber si entran
 * todas sin esa celda —y entonces no hace falta mostrarla—, y sólo si no entran, otra
 * vez reservándole su mínimo.
 *
 * Devuelve al menos 1: una fila con sólo "Ver más" no dice nada. Si esa única
 * miniatura no entrara —una ventana absurdamente angosta—, `Ver más` se queda en su
 * mínimo y la fila desborda; es preferible a no mostrar ninguna creación.
 */
function cuantasCreacionesEntran(
  ancho: number,
  ratios: number[],
  reserva: number,
  alto = ALTO_CREACION_PX,
  gap = GAP_CREACION_PX,
): number {
  let usado = reserva;
  let n = 0;
  for (const ratio of ratios) {
    const costo = alto * ratio + gap;
    if (usado + costo > ancho) break;
    usado += costo;
    n += 1;
  }
  return Math.max(1, n);
}

const MS_POR_MODO = 5000;

/**
 * Apertura del hueco de la bajada en las cards de modo (ADR-2026-07-31-titulo-cards-modo-apoya-abajo): misma curva y
 * duración que la expansión de la card, para que se lea como parte de la card
 * creciendo y no como una animación de texto suelta.
 *
 * La OPACIDAD no va acá porque es asimétrica (ADR-2026-07-31-bajada-cards-modo-aparece-hueco): `truncate` le pone
 * `overflow: hidden` a la bajada, así que mientras el hueco se abre el texto se ve
 * recortado por arriba. Entrando, la opacidad espera a que el hueco dé la altura de
 * una línea (17,2px de los 20px finales, que con esta curva llega al ~58% de los
 * 700ms) y recién ahí aparece. Saliendo hace lo contrario: se apaga rápido, antes de
 * que el hueco se cierre y la recorte.
 */
const TRANSICION_ALTO_BAJADA =
  'max-height 700ms cubic-bezier(0.4, 0, 0.2, 1), margin-top 700ms cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Copy de la tira, al mínimo: sobre la imagen no hay lugar para el título largo ni
 * para la descripción completa del catálogo, y compitiendo con la foto se leen peor.
 * Nombre corto + una línea de qué hace. El texto largo sigue vivo en `MODES`, que es
 * lo que muestran `/modos` y la home real.
 */
const COPY_TIRA: Record<string, { titulo: string; bajada: string }> = {
  // "Automática" y "Modelo" en vez de "Imagen automática" / "Imagen con modelo": en la
  // card angosta quedan 82 px de texto y esos títulos miden 130 y 134 px, así que
  // envolvían en dos líneas y su bloque arrancaba 17 px más arriba que el de los
  // cortos — desalineados contra las otras cuatro. Con todos en una línea, la
  // alineación sale sola. Estos títulos son ahora el nombre del modo también en la
  // sidebar (ADR-2026-07-31-solo-nombre-modo-sidebar-toma); el largo del catálogo ya no se muestra en esta pantalla
  // —estaba en el `title` de la card y se sacó en ADR-2026-07-31-tira-modos-muestra-tooltip-nativo—.
  auto: { titulo: 'Automática', bajada: 'La IA arma la escena' },
  modelo: { titulo: 'Modelo', bajada: 'Sobre un modelo IA' },
  catalogo: { titulo: 'Catálogo', bajada: 'Varias vistas en una tanda' },
  // `catalogo-muebles` e `infografia` faltaban acá (ADR-2026-07-31-nombres-cortos-muebles-infografia).
  // Sin entrada, los dos caían al fallback —el `title` del preset, de 27 y 30
  // caracteres— y el ancho de la card lo recortaba a "Catál…" e "Infogr…": DOS
  // cards seguidas que decían "Catál…" y no se distinguían entre sí. Mientras
  // esto fue un preview no se notó; en producción es la home.
  //
  // El de muebles se llama "Catálogo" y no "Muebles" —
  // ADR-2026-08-05-el-catalogo-de-muebles-se-llama-catalogo. Es EL catálogo de ese rubro:
  // `catalogo-ia` está `ocultoEnHogarDeco` y éste es `soloHogarDeco`, así que los dos
  // nunca se ven juntos y la razón por la que se llamaba distinto —dos cards seguidas
  // diciendo "Catál…"— ya no existe. Decir "Muebles" obligaba a la marca de muebles a
  // traducir: en su cuenta, esto ES el catálogo.
  'catalogo-muebles': { titulo: 'Catálogo', bajada: 'Fondo, medidas y contexto' },
  infografia: { titulo: 'Infografía', bajada: 'La ficha técnica del producto' },
  'video-influencer': { titulo: 'Video', bajada: 'Un influencer IA lo presenta' },
  asistente: { titulo: 'Crear desde cero', bajada: 'Pedile contenido por chat' },
  anuncio: { titulo: 'Anuncio', bajada: 'Listo para publicar' },
};

/**
 * Etiqueta de un modo en la sidebar — ADR-2026-07-31-solo-nombre-modo-sidebar-toma.
 *
 * Sale de `COPY_TIRA`, la MISMA fuente que los títulos del carrusel, para que un
 * modo se llame igual en las dos partes de la pantalla. Antes la sidebar usaba
 * `etiquetaMenu` del catálogo ("Generar imágenes", "Imagen con modelo") mientras
 * la tira decía "Automática" y "Modelo": dos nombres para el mismo destino, a la
 * vista al mismo tiempo.
 *
 * Se deriva acá y no se toca `contenidoAtlas`: `etiquetaMenu` lo consume también
 * `EstiloWorkspace`, y esos títulos largos siguen siendo los correctos para un
 * menú que no tiene un carrusel al lado con el nombre corto.
 *
 * El fallback a `nombre` cubre a Plantillas y Estudio de Video, que no están en el
 * carrusel (ADR-2026-07-30-minimal-plantillas-categoria-creaciones-fila) y por lo tanto no tienen entrada en `COPY_TIRA`.
 */
function etiquetaSidebar(h: { id: string; nombre: string }): string {
  return COPY_TIRA[h.id]?.titulo ?? h.nombre;
}

/**
 * Nombre corto de un modo por su `path` — ADR-2026-07-31-home-mobile-hero-nav-sheet-generar.
 *
 * Misma fuente (`COPY_TIRA`) que la tira de desktop y la sidebar, expuesta para el
 * sheet de "Generar" en mobile: sus cards son chicas y el `title` del preset
 * ("Generar imagen automáticamente") no entra. Un modo tiene que llamarse igual en
 * las cuatro superficies, y con esto no hay una quinta tabla de nombres.
 */
export function nombreCortoModo(path: string, fallback: string): string {
  return COPY_TIRA[path]?.titulo ?? fallback;
}

/**
 * Ítems del menú de cuenta — ADR-2026-07-31-menu-cuenta-avatar-uno-solo. "Cerrar sesión" NO está acá: va aparte, detrás
 * del divisor, porque es la única acción destructiva del menú.
 *
 * "Centro de ayuda" va a `/ayuda`, igual que el ítem homónimo de la sidebar: son la
 * misma etiqueta y tienen que llevar al mismo lado. El contacto directo por WhatsApp
 * quedó como su propio control ("Whatsapp", en el encabezado), con su propio nombre.
 */
const ITEMS_MENU_CUENTA: {
  label: string;
  icono: string;
  ruta?: string;
  url?: string;
}[] = [
  // "Configuración" y no "Perfil": el destino (`/configuracion`) es la pantalla de
  // ajustes entera —cuenta, plan, marcas, idioma—, no una ficha de perfil, y desde
  // ADR-2026-08-04-configuracion-abre-en-cuenta abre justamente en Cuenta. El ícono
  // acompaña: el monigote se lee como "tu perfil" y repetía el del avatar de arriba.
  { label: 'Configuración', icono: 'settings', ruta: RUTA_PERFIL },
  // "Suscripción" navega a `/subscription`, la página real de planes
  // (ADR-2026-08-04-suscripcion-navega-a-subscription, supersede
  // ADR-2026-08-04-suscripcion-abre-el-catalogo-sin-escala). El overlay del catálogo
  // sigue siendo lo que abre "Mejorar plan" de la tarjeta de cuenta; el ítem del menú
  // ahora lleva a la vista completa.
  { label: 'Suscripción', icono: 'credit_card', ruta: '/subscription' },
  { label: 'Centro de ayuda', icono: 'support_agent', ruta: '/ayuda' },
];

/**
 * Menú de cuenta que se abre desde el avatar — ADR-2026-07-31-menu-cuenta-avatar-uno-solo.
 *
 * Un solo componente para los dos avatares (el del saludo, arriba a la derecha, y el
 * de la tarjeta de cuenta, abajo en la sidebar): son el mismo menú, y duplicarlo
 * garantizaba que se desincronizaran.
 *
 * Se replica la ESTRUCTURA de la referencia —cabecera con nombre y mail, ítems con
 * ícono, divisor y cerrar sesión al final— no su paleta: la referencia es oscura
 * porque esa app lo es, y acá el shell es claro.
 *
 * `variante` decide el anclaje, que es lo único que cambia entre los dos:
 * - `home`: cuelga del avatar, alineado a la derecha. `absolute`, así acompaña al
 *   contenido si la página scrollea.
 * - `sidebar`: sale hacia la derecha del avatar y crece hacia arriba. Va `fixed` y
 *   con coordenadas medidas al abrir, porque el `<aside>` tiene `overflow-hidden`
 *   y un hijo absoluto quedaría cortado por el borde del panel.
 *
 * El menú se puede abrir desde AFUERA del avatar (`ref.alternar()`): en la sidebar
 * lo dispara la tarjeta de cuenta entera, que antes navegaba al perfil. El
 * disparador no puede envolver a la tarjeta —adentro vive "Mejorar plan" y anidar
 * botones es HTML inválido—, así que la tarjeta llama al handle. `zona` es esa
 * misma tarjeta: sin ella, el click de apertura cuenta como "click afuera", cierra
 * el menú en `mousedown` y lo vuelve a abrir en `click`, con lo que la tarjeta
 * nunca lo cerraba.
 */
type MenuCuentaHandle = { alternar: () => void };

/**
 * Posición del menú, medida al abrir. Los cuatro lados son opcionales porque cada
 * anclaje usa dos: la sidebar `left`+`bottom` (crece hacia arriba, a la derecha del
 * panel) y el encabezado `top`+`right` (cuelga del avatar).
 */
type Coords = { left?: number; right?: number; top?: number; bottom?: number };

const MenuCuenta = forwardRef<
  MenuCuentaHandle,
  {
    variante: 'home' | 'sidebar';
    children: ReactNode;
    /** Área que cuenta como "adentro" del menú además del propio menú. */
    zona?: RefObject<HTMLElement | null>;
  }
>(function MenuCuenta({ variante, children, zona }, ref) {
  const navigate = useNavigate();
  // Identidad y plan reales (ADR-2026-07-31-shell-nuevo-datos-reales). La `etiqueta` accesible se arma
  // acá y ya no la pasa cada llamador: los tres pasaban exactamente el mismo
  // string, y con el nombre viniendo de sesión eso obligaba a que los tres lo
  // leyeran del contexto sólo para reenviarlo.
  const { nombreCompleto, email, planLabel } = useContext(CuentaContext);
  const etiqueta = nombreCompleto
    ? `${nombreCompleto} — ${planLabel}`
    : 'Abrir menú de cuenta';
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);
  // El menú va PORTALEADO a `document.body`, así que ya no es descendiente de
  // `contenedor` y hay que preguntarle por separado si el click cayó adentro.
  const menuEl = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        contenedor.current?.contains(t) ||
        menuEl.current?.contains(t) ||
        zona?.current?.contains(t)
      )
        return;
      setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto, zona]);

  const alternar = () => {
    if (!abierto && disparador.current) {
      const r = disparador.current.getBoundingClientRect();
      if (variante === 'sidebar') {
        // Se ancla al borde derecho del PANEL, no al del avatar: colgado del avatar,
        // con la sidebar expandida el menú caía encima de la lista de herramientas.
        const panel = disparador.current.closest('aside')?.getBoundingClientRect();
        // Crece hacia ARRIBA: en la sidebar el disparador está abajo de todo y un menú
        // que baje se saldría de la ventana.
        //
        // El piso es el de la TARJETA (`zona`), no el del avatar: el avatar vive en la
        // fila de arriba de la tarjeta, así que anclando ahí el menú terminaba flotando
        // a media altura, sin apoyarse en nada. Con el borde de abajo alineado, el menú
        // y la tarjeta cierran en la misma línea. Sin `zona` —la sidebar colapsada, que
        // no tiene tarjeta— sigue valiendo el pie del avatar.
        // Los 2px de más bajan el menú lo justo para que su sombra no se lea como si
        // arrancara arriba del borde de la tarjeta.
        const piso = (zona?.current ?? disparador.current).getBoundingClientRect().bottom;
        setCoords({
          left: (panel?.right ?? r.right) + 10,
          bottom: window.innerHeight - piso - 2,
        });
      } else {
        // `home`: cuelga del avatar, alineado a su borde derecho. Antes era un
        // `absolute` contra el contenedor; con el portal el contenedor ya no es el
        // ancestro posicionado, así que se mide igual que el de la sidebar.
        setCoords({ top: r.bottom + 8, right: window.innerWidth - r.right });
      }
    }
    setAbierto((v) => !v);
  };

  useImperativeHandle(ref, () => ({ alternar }));

  const cerrarYNavegar = (ruta: string) => {
    setAbierto(false);
    navigate(ruta);
  };

  const claseItem =
    'flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-[13.5px] font-[500] text-black/75 transition-colors hover:bg-black/[0.04] hover:text-black';

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-label={etiqueta}
        className="block rounded-xl"
      >
        {children}
      </button>

      {abierto && createPortal(
        <div
          ref={menuEl}
          role="menu"
          // El menú corta la propagación de sus propios clicks. En la sidebar vive
          // DENTRO de la tarjeta de cuenta, que es clickeable (abre este mismo menú):
          // sin esto, elegir un ítem navegaba y en el mismo gesto la tarjeta volvía a
          // alternar el menú. Un portal NO cambia esto —los eventos de React siguen
          // burbujeando por el árbol de componentes, no por el DOM—.
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ position: 'fixed', ...coords }}
          className="animate-fade-in z-[120] w-[268px] rounded-2xl border border-black/[0.08] bg-white p-1.5 shadow-[0_16px_44px_rgba(16,24,45,0.16)]"
        >
          {/* Cabecera: quién sos. Sin acción propia —en la referencia tampoco la
              tiene—; "Perfil" es el ítem que lleva a editarlo. */}
          <div className="flex items-center gap-2.5 px-2.5 pb-2 pt-2.5">
            <AvatarUsuario px={40} />
            <div className="min-w-0 flex-1">
              {/* Sin nombre cargado, el mail sube a la línea principal en vez de
                  dejar un renglón vacío: es el identificador que el usuario sí
                  reconoce, y siempre existe con sesión. */}
              <p className="truncate text-[14px] font-semibold leading-tight">
                {nombreCompleto ?? email}
              </p>
              {nombreCompleto && email && (
                <p className="mt-0.5 truncate text-[12.5px] leading-tight text-black/45">
                  {email}
                </p>
              )}
            </div>
          </div>

          <div className="mt-1 flex flex-col gap-0.5">
            {ITEMS_MENU_CUENTA.map((item) =>
              item.url ? (
                <a
                  key={item.label}
                  role="menuitem"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setAbierto(false)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.label}
                  role="menuitem"
                  type="button"
                  onClick={() => cerrarYNavegar(item.ruta!)}
                  className={claseItem}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
                    {item.icono}
                  </span>
                  {item.label}
                </button>
              ),
            )}
          </div>

          <div className="my-1.5 h-px bg-black/[0.07]" />

          {/* Cierre de sesión REAL, el mismo que el Sidebar de producción
              (`lib/cerrarSesion`): es la única parte del menú que no puede ser
              decorativa sin volverse mentirosa. */}
          <button
            role="menuitem"
            type="button"
            onClick={async () => {
              setAbierto(false);
              await cerrarSesion();
            }}
            className={claseItem}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
              logout
            </span>
            Cerrar sesión
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
});

/**
 * Foto de perfil de una marca: el logo cargado si lo hay, y si no la inicial del
 * nombre en el chip azulado — ADR-2026-08-04-la-marca-se-ve-con-su-logo.
 *
 * La inicial es el RESPALDO y no la identidad: una marca con logo tiene que verse con
 * su logo en todos lados. El menú desplegable ya hacía esto; la tarjeta de la sidebar
 * —la que se ve siempre, incluso plegada— seguía en la inicial.
 *
 * Si la URL del logo no carga (borrado del bucket, firma vencida) vuelve a la inicial
 * en vez de dejar el ícono de imagen rota. `rota` guarda la URL que falló y no un
 * booleano, así el respaldo se levanta solo al cambiar de marca —sin efecto de reset—.
 *
 * La caja (lado y radio) la pone quien lo usa: 35px en la tarjeta para atarse al alto
 * del bloque de texto, 28 en el menú.
 */
export function AvatarMarca({
  logo,
  nombre,
  className,
  claseInicial,
}: {
  logo: string | null;
  nombre: string;
  className: string;
  claseInicial: string;
}) {
  const [rota, setRota] = useState<string | null>(null);

  if (logo && rota !== logo) {
    return (
      <img
        src={logo}
        alt=""
        onError={() => setRota(logo)}
        className={`${className} border border-black/[0.06] bg-white object-contain`}
      />
    );
  }
  return (
    <span
      className={`${className} grid place-items-center bg-[#EBEDF7] font-bold text-brand-primary ${claseInicial}`}
    >
      {nombre?.[0] ?? '?'}
    </span>
  );
}

/**
 * Menú de marcas que se abre desde la tarjeta de marca de la sidebar —
 * ADR-2026-08-03-tarjeta-marca-abre-menu-de-marcas.
 *
 * La tarjeta antes navegaba directo a `/mi-marca`. Ahora despliega: las marcas de la
 * cuenta (con la activa marcada, y cambiar es un click), "Editar marca actual" —el
 * destino viejo, que no se pierde— y "Añadir nueva marca".
 *
 * Es un menú y no un modal como el del Sidebar de producción: en este shell el bloque
 * de identidad de arriba a la derecha ya despliega un menú anclado a su disparador, y
 * la tarjeta de marca es el mismo tipo de control. Un modal centrado para elegir entre
 * dos ítems tapa la pantalla entera para nada.
 *
 * Va `fixed` con coordenadas medidas al abrir, por lo mismo que `MenuCuenta`: el
 * `<aside>` tiene `overflow-hidden` y un hijo absoluto quedaría cortado por el borde
 * del panel. Crece hacia ABAJO porque la tarjeta vive arriba de todo.
 */
function MenuMarcas({
  marcas,
  marcaActivaId,
  cambiarMarca,
  cambiando,
  onEditarMarca,
  children,
}: {
  marcas: BrandRow[];
  marcaActivaId: string | null;
  cambiarMarca: (id: string) => void;
  cambiando: boolean;
  /** Destino de "Editar marca actual" — lo pone el shell, con su fade. */
  onEditarMarca: () => void;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);
  // El plan se consulta al INTENTAR crear, no al abrir el menú: la gran mayoría de
  // las aperturas es para cambiar de marca, y una consulta por apertura sería
  // gratuita sólo en apariencia.
  const [pidiendoPlan, setPidiendoPlan] = useState(false);
  /**
   * Multi-marca es una función de plan pago. Cuando el plan no la incluye se abre el
   * CATÁLOGO —el mismo que abren los ítems con candado de la sidebar y las cards
   * bloqueadas de la tira— y no `SubscriptionLimitModal`
   * (ADR-2026-08-03-anadir-marca-bloqueada-abre-el-catalogo). Antes eran dos carteles
   * distintos para la misma situación: "esto viene con otro plan".
   */
  const { abrirPricing } = useContext(CuentaContext);
  const contenedor = useRef<HTMLDivElement | null>(null);
  const disparador = useRef<HTMLButtonElement | null>(null);
  // Portaleado a `document.body` (ver el comentario del `createPortal` abajo), así que
  // el menú ya no es descendiente de `contenedor` y se pregunta aparte.
  const menuEl = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!abierto) return;
    const afuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (contenedor.current?.contains(t) || menuEl.current?.contains(t)) return;
      setAbierto(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('mousedown', afuera);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', afuera);
      document.removeEventListener('keydown', escape);
    };
  }, [abierto]);

  const alternar = () => {
    if (!abierto && disparador.current) {
      const r = disparador.current.getBoundingClientRect();
      // Se abre AL COSTADO, como el menú de perfil del pie
      // (ADR-2026-08-03-menu-marcas-al-costado): anclado al borde derecho del PANEL
      // —no al de la tarjeta— para que los dos menús de la sidebar salgan de la
      // misma línea vertical, con o sin la sidebar colapsada.
      //
      // Colgado hacia abajo, como estaba, el menú tapaba la lista de herramientas
      // entera: la tarjeta de marca vive arriba de todo y el menú tiene cinco ítems.
      //
      // La diferencia con el de perfil es hacia dónde crece. Aquél se ancla por el
      // piso porque su disparador está abajo de todo; éste se ancla por el techo,
      // alineado con el borde de arriba de la tarjeta, porque crece hacia abajo.
      const panel = disparador.current.closest('aside')?.getBoundingClientRect();
      setCoords({ left: (panel?.right ?? r.right) + 10, top: r.top });
    }
    setAbierto((v) => !v);
  };

  const elegir = (id: string) => {
    setAbierto(false);
    cambiarMarca(id);
  };

  /**
   * Multi-marca es una función de plan pago (`planAllowsMultiBrand`). El criterio es
   * el MISMO que el del Sidebar de producción —se consulta el plan y, si no alcanza,
   * no se crea— y no una regla nueva: dos criterios para la misma puerta se
   * desincronizan al primer cambio de planes.
   *
   * Lo que cambia acá es lo que se muestra cuando no alcanza: el catálogo de planes
   * en vez de `SubscriptionLimitModal`
   * (ADR-2026-08-03-anadir-marca-bloqueada-abre-el-catalogo).
   */
  const crearMarca = async () => {
    if (pidiendoPlan) return;
    setPidiendoPlan(true);
    try {
      const plan = await getUserPlan();
      setAbierto(false);
      if (!planAllowsMultiBrand(plan)) {
        // Mismo motivo que los otros accesos bloqueados: trae el copy "Desbloqueá
        // todo el potencial de Estudio Atlas", que no habla de créditos —acá el saldo
        // no tiene nada que ver, es el plan—.
        abrirPricing('bloqueado');
        return;
      }
      navigate('/redes-sociales/descripcion-brand?mode=additional');
    } finally {
      setPidiendoPlan(false);
    }
  };

  const claseItem =
    'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-[13.5px] font-[500] text-black/75 transition-colors hover:bg-black/[0.04] hover:text-black disabled:opacity-50';

  return (
    <div ref={contenedor} className="relative">
      <button
        ref={disparador}
        type="button"
        onClick={alternar}
        aria-haspopup="menu"
        aria-expanded={abierto}
        className="block w-full rounded-xl"
      >
        {children}
      </button>

      {abierto && coords && createPortal(
        <div
          ref={menuEl}
          role="menu"
          // Mismo corte que en `MenuCuenta`: el menú no deja que sus clicks lleguen a
          // lo que haya arriba, que en la sidebar es una tarjeta clickeable.
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          // El alto lo pone el CONTENIDO — ADR-2026-08-04-el-menu-de-marcas-crece-con-las-marcas.
          // El tope es lo que queda de ventana desde donde arranca el menú (menos
          // 12px de aire abajo): con pocas marcas no se nota, y con muchas el menú
          // crece hasta el borde en vez de recortarse a una altura fija.
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            maxHeight: `calc(100dvh - ${coords.top}px - 12px)`,
          }}
          className="animate-fade-in z-[120] flex w-[268px] flex-col rounded-2xl border border-black/[0.08] bg-white p-1.5 shadow-[0_16px_44px_rgba(16,24,45,0.16)]"
        >
          {/* Rótulo y no un ítem: la lista necesita decir de qué es, y con una sola
              marca el menú si no arrancaría directo en las acciones. */}
          <p className="px-2.5 pb-1.5 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
            Tus marcas
          </p>

          {/* Sin alto fijo: la lista se estira con las marcas que haya y sólo scrollea
              si el menú entero ya llegó al borde de la ventana (el `maxHeight` de
              arriba). `min-h-0` es lo que le permite encogerse dentro del flex —
              sin eso, un hijo flex no baja de su contenido y el scroll nunca aparece,
              con el menú desbordando la pantalla. */}
          <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
            {marcas.map((m) => {
              const activa = m.id === marcaActivaId;
              return (
                <button
                  key={m.id}
                  role="menuitem"
                  type="button"
                  onClick={() => elegir(m.id)}
                  disabled={cambiando}
                  className={claseItem}
                >
                  {/* El logo si lo hay, y si no la inicial en el mismo chip que usa la
                      tarjeta: así una marca sin logo no se ve como un hueco roto.
                      `rounded-sm` = 8px en este config (no el 2px de Tailwind por
                      defecto: la escala está pisada en `index.css`). Ojo con `lg`,
                      que acá son 16px y a 28px de lado recorta un círculo. */}
                  <AvatarMarca
                    logo={m.logo_url}
                    nombre={m.name}
                    className="h-7 w-7 shrink-0 rounded-sm"
                    claseInicial="text-[11px]"
                  />
                  {/* Nombre y rubro apilados, igual que en la tarjeta que abre este
                      menú: con varias marcas del mismo rubro —o con nombres cortos
                      como "Belle" / "Ropa"— el nombre solo no alcanzaba para saber
                      cuál es cuál. El rubro sale de `brand_profiles.rubro`; si la
                      marca no lo tiene, no se reserva la línea. */}
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{m.name}</span>
                    {m.rubro && (
                      <span className="truncate text-[11.5px] font-[400] leading-[15px] text-black/45">
                        {m.rubro}
                      </span>
                    )}
                  </span>
                  {activa && (
                    <span className="material-symbols-rounded shrink-0 text-[18px] text-brand-primary">
                      check
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="my-1.5 h-px bg-black/[0.07]" />

          <button
            role="menuitem"
            type="button"
            onClick={() => {
              setAbierto(false);
              onEditarMarca();
            }}
            className={claseItem}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px] text-black/55">
              tune
            </span>
            Editar marca actual
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={crearMarca}
            disabled={pidiendoPlan}
            className={`${claseItem} text-brand-primary hover:text-brand-primary`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">add</span>
            Añadir nueva marca
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Una card de la tira. Es un componente aparte para poder manejar el `<video>` por
 * ref: la reproducción no se puede dejar en props (`autoPlay` sólo cuenta al montar)
 * si además hay que **frenar al terminar**.
 *
 * El video se reproduce UNA vez al volverse activo y se queda en el último cuadro.
 * No hay `loop`: aunque el mouse siga encima, no vuelve a arrancar; sólo se rebobina
 * cuando la card deja de estar activa.
 */
function CardModo({
  modo,
  esActivo,
  bloqueado = false,
  onActivar,
  onIr,
  onVerEjemplos,
  tema,
}: {
  modo: HerramientaAtlas;
  esActivo: boolean;
  /** El plan del usuario no incluye este modo: se marca con candado. */
  bloqueado?: boolean;
  onActivar: () => void;
  /** Click en la card: abrir el modo, o el catálogo de planes si está bloqueado. */
  onIr: () => void;
  /** Sólo para bloqueadas: el visor de ejemplos ya cargados del modo. */
  onVerEjemplos: () => void;
  tema: TemaMinimal;
}) {
  const video = useRef<HTMLVideoElement | null>(null);
  const copy = COPY_TIRA[modo.id] ?? { titulo: modo.nombre, bajada: '' };

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    if (esActivo) {
      el.currentTime = 0;
      el.play().catch(() => {
        /* autoplay bloqueado: queda el primer cuadro */
      });
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [esActivo]);

  return (
    // La card dejó de ser UN botón: ahora es un contenedor con dos controles reales
    // adentro —la superficie y la pastilla— porque cada uno lleva a un lado distinto
    // cuando el modo está bloqueado (ADR-2026-08-03-modo-bloqueado-abre-pricing).
    // El reparto de ancho pasó acá, que es el item de flex.
    //
    // El reparto y su animación van INLINE, no en clases: `index.css` tiene una
    // regla global para `button:not(:disabled)` que fija `transition-property`
    // (una lista sin `flex-grow`) y 150ms de duración. Es un selector de elemento,
    // así que le gana a las utilidades de Tailwind y la transición no corría. Ahora
    // el contenedor es un `div` y la regla no lo alcanza, pero el inline se queda:
    // es explícito y no depende de qué elemento sea.
    <div
      style={{
        flexGrow: esActivo ? 2.8 : 0.62,
        flexShrink: 1,
        flexBasis: 0,
        transition: 'flex-grow 700ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      className={`group relative overflow-hidden rounded-2xl border border-black/[0.06] ${tema.hueco}`}
    >
    <button
      onClick={onIr}
      onMouseEnter={onActivar}
      onFocus={onActivar}
      aria-current={esActivo}
      // Sin `title`: el atributo nativo dispara el globito gris del navegador al
      // dejar el mouse quieto sobre la card, tapando la imagen justo cuando se la
      // está mirando (ADR-2026-07-31-tira-modos-muestra-tooltip-nativo). El nombre del modo ya está impreso sobre la card,
      // así que el botón conserva su nombre accesible por contenido.
      //
      // Ese contenido ahora es sólo la imagen (el título vive en la capa de texto, que
      // quedó afuera para poder tener su propio botón), así que el nombre accesible se
      // declara a mano.
      aria-label={bloqueado ? `${copy.titulo} — ver planes` : copy.titulo}
      className="absolute inset-0 h-full w-full text-left"
    >
      {modo.video ? (
        <video
          ref={video}
          src={modo.video}
          // Sin `poster` (Anuncio) el navegador dibuja el primer cuadro del propio
          // video, que es justo la portada que se quiere.
          poster={modo.poster || undefined}
          muted
          playsInline
          preload={modo.poster ? 'none' : 'metadata'}
          // Techo de reproducción: aunque el archivo dure más, se frena al llegar al
          // tiempo que la tira le da a cada modo. Sin esto, con el mouse encima (que
          // pausa la rotación) el video seguiría hasta el final.
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (v.currentTime >= MS_POR_MODO / 1000) v.pause();
          }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <img
          src={modo.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}

      {/* Velo NEGRO sobre las que no están en foco: oscurece de verdad. Bajarle la
          opacidad a la card entera, sobre este fondo claro, las aclara en vez de
          oscurecerlas. Va encima de la imagen y debajo del texto, así el label sigue
          leyéndose. */}
      <span
        aria-hidden="true"
        // Bloqueada NUNCA se destapa del todo: al activarse baja a 35% en vez de a 0,
        // así el modo se ve pero se sigue leyendo apagado contra los que sí están
        // disponibles. Sin eso, la card activa con candado parecía tan usable como
        // las otras y el candado quedaba como un adorno.
        //
        // En reposo el velo depende de si está bloqueada
        // (ADR-2026-08-03-velo-mas-suave-en-los-modos-disponibles): la bloqueada se
        // queda en 60% —ahí el velo dice "esto no lo podés usar"— y la disponible baja
        // a la mitad, 30%, donde sólo tiene que apagarla respecto de la que está en
        // foco. Con las dos en 60% un modo disponible y uno bloqueado se veían igual
        // de apagados y lo único que los distinguía era el candado.
        className={`absolute inset-0 bg-black transition-opacity duration-500 ${
          !esActivo
            ? bloqueado
              ? 'opacity-60'
              : 'opacity-30'
            : bloqueado
              ? 'opacity-35'
              : 'opacity-0'
        }`}
      />
    </button>

      {/* Degradé sólo para que el texto se lea; se refuerza en el activo. El padding
          es el MISMO en los dos estados: con `px-3` colapsado y `px-4` activo, el
          texto se corría 4 px al expandirse y las cards quedaban desalineadas entre
          sí. Lo que cambia con el estado es sólo la fuerza del degradé.

          `pointer-events-none`: esta capa está ENCIMA del botón de la card, así que
          sin esto se comía el click y el hover de toda la mitad de abajo. Sólo la
          pastilla vuelve a escuchar (`pointer-events-auto`), que es la que tiene su
          propia acción. */}
      <span
        className={`pointer-events-none absolute inset-x-0 bottom-0 flex items-end gap-3 bg-gradient-to-t to-transparent px-5 pb-5 pt-12 ${
          esActivo ? 'from-black/75 via-black/30' : 'from-black/60 via-black/15'
        }`}
      >
        <span className="min-w-0 flex-1">
          {/* Cuerpo FIJO, sin transición: el título es el mismo texto en los dos
              estados, así que cualquier cambio de tamaño se ve como movimiento y no
              como aparición.

              `truncate` es acá una GARANTÍA de alineación, no un recorte esperado: los
              seis títulos entran en una línea a este ancho (ver `COPY_TIRA`), y esto
              impide que una pantalla más angosta devuelva el wrap a dos líneas —que es
              lo que descolocaba unas cards contra otras—. Si alguna vez recorta, es
              señal de que el título es largo de más para la tira. */}
          <span className="block truncate text-[14px] font-semibold leading-tight text-white">
            {copy.titulo}
          </span>
          {/* La bajada NO reserva su línea cuando la card está colapsada: si la
              reservara, el título quedaría 21 px por encima del borde en las seis
              cards angostas en vez de apoyado abajo.

              El costo es que el título sube al activarse la card, para hacerle lugar.
              Se le da la MISMA curva y duración que a la expansión de la card
              (700 ms) para que se lea como parte de la card creciendo y no como una
              animación de texto aparte; la opacidad va más corta, así el texto
              termina de aparecer cuando el hueco ya está abierto. */}
          {copy.bajada && (
            <span
              style={{
                transition: esActivo
                  ? `${TRANSICION_ALTO_BAJADA}, opacity 280ms ease-out 420ms`
                  : `${TRANSICION_ALTO_BAJADA}, opacity 160ms ease-in`,
              }}
              className={`block truncate text-[12.5px] leading-snug text-white/75 ${
                esActivo ? 'mt-1 max-h-5 opacity-100' : 'mt-0 max-h-0 opacity-0'
              }`}
            >
              {copy.bajada}
            </span>
          )}
        </span>

        {/* Pastilla de acción — ahora un `button` de verdad, hermano del de la card y
            no hijo (anidar botones es HTML inválido). Sólo en la activa: en la angosta
            no entra. Entra con `animate-fade-in`, la animación del proyecto (opacidad
            + un scale mínimo): al montarse condicionalmente, una `transition` no
            correría —el elemento nace con su estilo final—, así que tiene que ser
            keyframes.

            Con candado los dos controles llevan a lados DISTINTOS: la card al catálogo
            de planes y la pastilla a los ejemplos ya cargados del modo
            (ADR-2026-08-03-modo-bloqueado-abre-pricing). Sin candado hacen lo mismo,
            que es entrar al modo. */}
        {esActivo && (
          <button
            type="button"
            onClick={bloqueado ? onVerEjemplos : onIr}
            className="animate-fade-in pointer-events-auto shrink-0 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#16181D] shadow-sm"
          >
            {bloqueado ? 'Ver ejemplos' : 'Probar modo'}
          </button>
        )}
      </span>

      {/* Candado del gating por plan (`getModePlanLock`). Al CENTRO de la card y al
          doble de tamaño: arriba a la derecha y a 28px se leía como un badge de
          esquina —de esos que se ignoran—, cuando es el dato que explica por qué la
          card está apagada. Al centro no compite con el título, que vive abajo.
          Se mantiene visible en los dos estados porque es información sobre el modo,
          no un adorno del foco. */}
      {bloqueado && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm"
        >
          <span className="material-symbols-rounded text-[30px]">lock</span>
        </span>
      )}
    </div>
  );
}

/**
 * Tira de modos: **todos visibles a la vez**, sin scroll horizontal. El activo se
 * expande y los demás quedan como columnas angostas; la transición es del `flex-grow`,
 * que anima suave sin reflow del resto de la home.
 *
 * Va pasando solo cada `MS_POR_MODO`. Se pausa al pasar el mouse por la tira, y el
 * modo apuntado se vuelve el activo — así se puede mirar uno sin pelearle al reloj.
 * Con `prefers-reduced-motion` no arranca el pase automático.
 *
 * Sólo el activo reproduce su carátula en video, y una sola vez (ver `CardModo`); los
 * demás muestran el póster. Con seis videos corriendo a la vez la home pesaría de más.
 */
function CarruselModos({ modos, tema }: { modos: ModoHome[]; tema: TemaMinimal }) {
  const [activo, setActivo] = useState(0);
  const [pausado, setPausado] = useState(false);
  const { conFadeDeSalida, abrirPricing, rubroMarca } = useContext(CuentaContext);

  // La tira ya no lista `CARRUSEL_MODOS` (ADR-2026-07-31-inicio-se-funde-interfaz-v2): qué modos hay
  // —y en qué orden— lo decide `InterfazV2`, que es quien conoce los feature flags
  // y el rubro del usuario. De este catálogo sale sólo la PRESENTACIÓN (carátula en
  // video, póster), que es lo que el catálogo tiene y los presets no.
  //
  // Un modo sin entrada en el catálogo no se descarta: cae a la imagen del propio
  // preset. Si se filtrara, un modo nuevo desaparecería de la home sin que nadie lo
  // note — que es exactamente el bug que ya tuvo la traducción de rutas.
  const cards = useMemo(
    () =>
      modos.map((m) => {
        const delCatalogo = CARRUSEL_MODOS.find((c) => c.id === m.path);
        // La carátula propia del rubro gana sobre la del catálogo y va SIN video
        // (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro): el video de Catálogo
        // es la grilla de moda, justo lo que la portada del rubro viene a reemplazar.
        const delRubro = portadaDeRubro(m.path, rubroMarca);
        if (delRubro) {
          return {
            ...m,
            arte: { id: m.path, nombre: m.titulo, poster: delRubro, video: undefined },
          };
        }
        return {
          ...m,
          // El respaldo se decide por la CARÁTULA y no por la entrada
          // (ADR-2026-08-05-la-tira-cae-cuando-la-entrada-no-trae-caratula): el catálogo
          // sale de `MODES`, así que TODOS los modos tienen entrada —incluidos los que
          // no traen ni póster ni video, como Infografía— y con un `??` sobre la entrada
          // esa card quedaba en el gris del contenedor vacío.
          arte:
            delCatalogo?.poster || delCatalogo?.video
              ? delCatalogo
              : {
                  id: m.path,
                  nombre: m.titulo,
                  poster: m.imagen,
                  video: undefined,
                },
        };
      }),
    [modos, rubroMarca],
  );

  useEffect(() => {
    if (pausado) return;
    if (cards.length === 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(
      () => setActivo((i) => (i + 1) % cards.length),
      MS_POR_MODO,
    );
    return () => window.clearInterval(id);
  }, [pausado, cards.length]);

  if (cards.length === 0) return null;

  return (
    <section className="mt-12">
      <div
        className="flex h-[420px] gap-2.5"
        onMouseLeave={() => setPausado(false)}
      >
        {cards.map((card, i) => (
          <CardModo
            key={card.path}
            modo={card.arte as HerramientaAtlas}
            tema={tema}
            esActivo={i === activo}
            bloqueado={card.bloqueado}
            onActivar={() => {
              setPausado(true);
              setActivo(i);
            }}
            // Bloqueado: la card va DERECHO al catálogo de planes
            // (ADR-2026-08-03-modo-bloqueado-abre-pricing). Antes abría el visor de
            // ejemplos, que es información y no una salida: el usuario tocaba un modo
            // que no tiene y terminaba mirando fotos, sin ver cómo destrabarlo. Los
            // ejemplos siguen a un click, ahora en su propia pastilla.
            //
            // Libre: el destino lo decide el llamador — abrir un modo pasa por el
            // guardado del scroll de la home de `InterfazV2`, y navegar por afuera se
            // lo saltearía. Lo que SÍ pone el shell es la transición: entrar desde la
            // tira se ve igual que entrar desde la sidebar
            // (ADR-2026-08-03-tira-modos-usa-fade-del-shell).
            onIr={() =>
              card.bloqueado
                ? abrirPricing('bloqueado')
                : conFadeDeSalida(card.onAbrir)
            }
            // `onAbrir` con candado es justamente el visor de ejemplos de `InterfazV2`
            // (`openLockedModeExamples`), así que la pastilla lo sigue usando tal cual.
            // Sin fade: es un overlay sobre la home, no un cambio de sección.
            onVerEjemplos={card.onAbrir}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Celda "Ver más": el acceso a la lista completa vive DENTRO de la grilla, como
 * sexta celda, a la misma altura que lo que muestra.
 *
 * Nació en las filas de Plantillas y desde ADR-2026-08-03-ver-mas-en-creaciones-recientes
 * la comparte también Creaciones recientes. Se extrajo en vez de copiarse: es la
 * misma pieza —mismo tamaño, mismo punteado, mismo hover azul— y duplicada se iban
 * a separar al primer retoque.
 *
 * `etiqueta` es el `aria-label`: el texto visible es siempre "Ver más", que sin
 * contexto no dice de qué, y cada sección sabe a dónde lleva la suya.
 *
 * `fondo` es lo único que cambia entre las dos, porque cada una se apoya sobre una
 * superficie distinta: en Plantillas la celda vive DENTRO de la tarjeta blanca de
 * la colección, así que va un tono por debajo (`vacio`) para no desaparecer contra
 * ella; en Creaciones recientes la grilla está sobre el lienzo gris pelado, y ahí
 * el blanco es lo que la empareja con las miniaturas de al lado
 * (ADR-2026-08-03-ver-mas-en-creaciones-recientes).
 */
function CeldaVerMas({
  etiqueta,
  onClick,
  tema,
  fondo,
  medidas,
  estilo,
}: {
  etiqueta: string;
  onClick: () => void;
  tema: TemaMinimal;
  /** Clase de fondo. Por defecto el de Plantillas, que fue el caso original. */
  fondo?: string;
  /**
   * Cómo se dimensiona la celda. El defecto es el de Plantillas —una columna más de
   * la grilla, con la proporción 3:4 de sus miniaturas—; Creaciones lo pisa porque su
   * fila es de alto fijo y ancho variable
   * (ADR-2026-08-03-creaciones-recientes-respetan-su-forma).
   */
  medidas?: string;
  /** Va con `medidas` cuando la proporción no se puede escribir como clase. */
  estilo?: React.CSSProperties;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={etiqueta}
      style={estilo}
      className={`group flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-black/[0.14] transition-colors hover:border-brand-primary/40 hover:bg-[#F3F5FD] ${medidas ?? `aspect-[3/4] ${CELDA_MINIATURA}`} ${fondo ?? tema.vacio}`}
    >
      <span
        className={`grid h-9 w-9 place-items-center rounded-full text-black/55 shadow-sm transition-colors group-hover:text-brand-primary ${tema.hueco}`}
      >
        <span className="material-symbols-rounded text-[20px]">arrow_forward</span>
      </span>
      <span className="text-[12.5px] font-medium text-black/60 transition-colors group-hover:text-brand-primary">
        Ver más
      </span>
    </button>
  );
}

/**
 * Plan del usuario para gatear secciones de la home —
 * ADR-2026-08-18-creaciones-recientes-solo-para-planes-pagos.
 *
 * Mismo arranque que `/modos`, el sheet de generar y el drawer de Mi marca: el cache
 * que llena el Sidebar (`atlas-user-plan`) para decidir ya en el primer paint, y el
 * valor real de Supabase después. No se lee de `CuentaContext` a propósito: ese
 * contexto expone `planLabel` (una etiqueta para mostrar, no el plan) y encima está
 * en su default inerte en mobile, donde `InterfazV2` monta las secciones FUERA del
 * shell — o sea que ahí no diría nada.
 *
 * `null` = todavía no se sabe. Para lo que se gatea con esto, desconocido cuenta
 * como gratis: es preferible que a un pago le aparezca la sección un instante
 * después a que a un gratis le parpadee una sección que no le corresponde.
 */
function usePlanUsuario(): string | null {
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlan(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);
  return plan;
}

/**
 * ¿Se muestra "Creaciones recientes"? Envoltorio del predicado puro
 * `muestraCreacionesRecientes` (`lib/seccionesHome.ts`), donde están la regla y el
 * porqué de cada default — ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda.
 *
 * Existe como HOOK COMPARTIDO, y no como un `if` adentro de la sección, porque lo
 * consumen DOS componentes que tienen que decir lo mismo: la sección
 * (`CreacionesRecientes`) y el ESQUELETO de la home (`HomeMinimalSkeleton`), que reserva
 * —o no— la fila. Si discrepan, el resultado es justo el parpadeo que hay que evitar: el
 * esqueleto promete una fila que después no aparece y la home entera sube de golpe.
 */
function useMuestraCreacionesRecientes(): boolean {
  const plan = usePlanUsuario();
  const { rubro, loaded: rubroResuelto } = useRubroMarcaActiva();
  return muestraCreacionesRecientes(plan, rubro, rubroResuelto);
}

/**
 * "Creaciones recientes" con las generaciones REALES del usuario —
 * ADR-2026-07-31-shell-nuevo-datos-reales.
 *
 * Reemplaza a `GrillaImagenes`, el bloque genérico "título + bajada + grilla" que
 * compartía con Plantillas. Se borró en vez de extenderlo: las tres cosas que esta
 * sección necesita —cada celda abre SU imagen y no la sección, un estado de carga
 * que no puede parpadear y un estado vacío— no existen en una grilla de catálogo, y
 * Plantillas hacía rato que renderizaba su propia grilla (con la celda "Ver más").
 * El "genérico" tenía un solo consumidor.
 *
 * Abrir una creación ya NO navega al historial: monta ese mismo componente en modo
 * "sólo visor" y la muestra ampliada acá (ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home).
 * Antes se navegaba con `state.expandImageId`, que era irse de la home para ver una
 * imagen. "Ver todo" y "Ver más" sí siguen yendo al historial: eso es una lista.
 */
function CreacionesRecientes({
  idCompany,
  idMarca,
  tema,
}: {
  idCompany: string | null;
  /** Marca activa: la sección muestra sólo lo generado para ELLA
   *  (ADR-2026-08-03-creaciones-recientes-por-marca). */
  idMarca: string | null;
  tema: TemaMinimal;
}) {
  const navigate = useNavigate();
  /**
   * Quién ve esta sección — ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda.
   * Era "sólo planes pagos"; ahora se esconde sólo en la prueba gratis DE MODA.
   *
   * El gate vive acá adentro y no en `SeccionesHome` porque así vale para las DOS
   * homes sin enhebrar una prop: desktop la monta desde `HomeMinimal` y mobile desde
   * `InterfazV2`, y las dos pasan por este componente. El esqueleto usa el MISMO hook,
   * que es lo que evita que prometa una fila que después no aparece.
   */
  const seVe = useMuestraCreacionesRecientes();
  // Si no se ve no se consulta nada: `null` como empresa es lo que el hook ya
  // entiende como "no hay a quién pedirle creaciones", así que la home de quien no
  // la ve no dispara ni la query a `imagenes_generadas` ni las firmas de Storage.
  const { items, cargando, hayMas } = useCreacionesRecientes(
    seVe ? idCompany : null,
    6,
    idMarca,
  );
  /**
   * Proporciones medidas sobre la miniatura ya cargada —
   * ADR-2026-08-03-creaciones-recientes-respetan-su-forma. Se llenan en el `onLoad`.
   *
   * Entran acá dos casos, y por eso lo medido le GANA a lo declarado:
   * - las creaciones sin `formato` en la base (una de cada cuatro);
   * - **todos los videos**, porque su formato declarado no es de fiar
   *   (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar).
   */
  const [ratiosMedidos, setRatiosMedidos] = useState<Record<string, number>>({});

  /**
   * Con qué proporción se dibuja: lo MEDIDO manda, lo declarado es la apuesta
   * inicial y 4:5 el respaldo mientras no haya ni una cosa ni la otra.
   */
  // `useCallback` y no `React.useCallback`: este módulo importa los hooks por nombre
  // y NO tiene `import React` (ver la nota del `useMemo` de abajo).
  const ratioDe = useCallback(
    (it: CreacionReciente) =>
      ratiosMedidos[it.id] ?? it.ratio ?? RATIO_CREACION_DEFECTO,
    [ratiosMedidos],
  );

  /**
   * Cuántas miniaturas entran, medido contra el ancho REAL de la fila —
   * ADR-2026-08-03-creaciones-entran-las-que-entran.
   *
   * Se mide el contenedor con un `ResizeObserver` y no se calcula desde el viewport:
   * el ancho depende de la columna del shell, de si la sidebar está colapsada y del
   * zoom, y cualquiera de esas cambia sin que cambie el viewport.
   *
   * `anchoFila = 0` significa "todavía no se midió" (primer render): ahí no se corta
   * nada, así el primer pintado no muestra una sola miniatura y después mete el resto.
   */
  const filaRef = useRef<HTMLDivElement | null>(null);
  const [anchoFila, setAnchoFila] = useState(0);
  useEffect(() => {
    const el = filaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(([entrada]) => {
      setAnchoFila(entrada.contentRect.width);
    });
    observador.observe(el);
    return () => observador.disconnect();
  }, [cargando, items.length]);

  /**
   * En mobile la fila scrollea y se muestran todas: ahí no entra ni una miniatura al
   * lado de "Ver más", y cortar la lista dejaría la sección con una sola imagen.
   */
  const [esAngosta, setEsAngosta] = useState(false);
  useEffect(() => {
    const consulta = window.matchMedia('(max-width: 639px)');
    const leer = () => setEsAngosta(consulta.matches);
    leer();
    consulta.addEventListener('change', leer);
    return () => consulta.removeEventListener('change', leer);
  }, []);

  /**
   * Qué miniaturas se dibujan y si hace falta "Ver más" —
   * ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas.
   *
   * "Ver más" aparece sólo cuando queda algo afuera, y hay dos formas de que quede
   * algo afuera: que el hook haya encontrado más de las que trae (`hayMas`), o que no
   * entren todas en el ancho disponible.
   *
   * El orden de las preguntas importa: primero se calcula sin reservarle lugar a la
   * celda, porque si entran todas así, esa celda no va — y reservarle lugar de entrada
   * habría dejado una miniatura afuera creando el motivo para mostrarla.
   */
  // `useMemo` y no `React.useMemo`: este módulo importa los hooks por nombre y NO
  // tiene `import React`, así que `React` no existe en runtime — era un
  // `ReferenceError` al renderizar las creaciones recientes, o sea la home entera.
  const { visibles, mostrarVerMas } = useMemo(() => {
    if (esAngosta || !anchoFila) {
      // Mobile (y el primer render, antes de medir): la fila scrollea y se muestran
      // todas, así que sólo puede quedar afuera lo que el hook no trajo.
      return { visibles: items, mostrarVerMas: hayMas };
    }
    const ratios = items.map(ratioDe);
    const sinReserva = cuantasCreacionesEntran(anchoFila, ratios, 0);
    if (sinReserva >= items.length && !hayMas) {
      return { visibles: items, mostrarVerMas: false };
    }
    const conReserva = cuantasCreacionesEntran(anchoFila, ratios, MIN_VER_MAS_PX);
    return { visibles: items.slice(0, conReserva), mostrarVerMas: true };
  }, [items, hayMas, ratioDe, anchoFila, esAngosta]);

  /**
   * Creación abierta en el visor — ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home.
   * Tocar una miniatura ya no navega al historial: monta ese mismo componente en modo
   * "sólo visor", que dibuja la imagen ampliada con todas sus acciones y nada más.
   */
  const [creacionAbierta, setCreacionAbierta] = useState<string | null>(null);
  /**
   * Las dos puntas de la transición del velo —
   * ADR-2026-08-03-el-visor-de-la-home-abre-con-transicion.
   *
   * `veloEncendido` lo prende un frame DESPUÉS de montar: si montara ya en
   * `opacity-100` no habría de dónde transicionar y el negro entraría de golpe.
   * `visorAbierto` lo apaga cuando el visor arranca su propia apertura, así los dos
   * se cruzan en vez de que uno reemplace al otro.
   */
  const [veloEncendido, setVeloEncendido] = useState(false);
  const [visorAbierto, setVisorAbierto] = useState(false);
  useEffect(() => {
    if (!creacionAbierta) {
      setVeloEncendido(false);
      setVisorAbierto(false);
      return;
    }
    // Doble rAF —el mismo patrón que usa `openLightbox` en el historial—: uno solo no
    // garantiza que el navegador haya pintado el estado inicial.
    let segundo = 0;
    const primero = requestAnimationFrame(() => {
      segundo = requestAnimationFrame(() => setVeloEncendido(true));
    });
    return () => {
      cancelAnimationFrame(primero);
      cancelAnimationFrame(segundo);
    };
  }, [creacionAbierta]);

  /** "Ver todo" y "Ver más": estos SÍ van al historial, que es una lista. */
  const irAlHistorial = () => navigate(RUTA_HISTORIAL);

  // Recién acá el corte, y no arriba: todos los hooks de este componente tienen que
  // haberse llamado igual en los dos casos.
  if (!seVe) return null;

  return (
    <section className="mt-12">
      {/* "Ver todo" PEGADO al título (12px), no empujado al borde derecho: sin la
          bajada la fila quedaba con el botón solo a media pantalla de distancia.
          `items-end` lo apoya en el pie del título en vez de centrarlo contra sus
          22px, que lo dejaban flotando a media altura. */}
      <div className="flex items-end gap-3">
        <div>
          <h2 className="text-[22px] font-bold tracking-[-0.01em]">Creaciones recientes</h2>
        </div>
        {/* "Ver todo" sólo con algo que ver: sobre el estado vacío mandaría al
            historial vacío, y la tarjeta de abajo ya ofrece la acción que sí sirve. */}
        {(cargando || items.length > 0) && (
          <button onClick={irAlHistorial} className={BOTON_VER_TODO}>
            Ver todo <MoveRight className="h-4 w-4" strokeWidth={2.25} />
          </button>
        )}
      </div>

      {cargando ? (
        // Seis celdas del MISMO tamaño que las reales: la sección no cambia de alto
        // al llegar los datos, así que lo de abajo (Plantillas, Ediciones rápidas)
        // no salta.
        <div className={`mt-5 ${FILA_CREACIONES}`}>
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              style={{ aspectRatio: RATIO_CREACION_DEFECTO }}
              className={`shrink-0 animate-pulse rounded-xl border border-black/[0.06] ${ALTO_CREACION} ${tema.hueco}`}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        // Vacío REAL: una cuenta nueva no generó nada todavía. Antes esto no podía
        // pasar porque las imágenes eran de demo, y la home le decía "lo último que
        // generaste" a alguien que no había generado nada.
        // `bg-white` y no `tema.vacio` (ADR-2026-08-04-el-estado-vacio-de-creaciones-en-una-linea):
        // el gris del token es casi el del lienzo y la caja se leía como un hueco.
        // Va literal acá y no en el tema porque `tema.vacio` lo comparte la celda
        // "Ver más" de Plantillas, que se pidió expresamente que NO fuera blanca
        // (ADR-2026-08-03-solo-el-ver-mas-de-creaciones-va-en-blanco).
        <div className="mt-5 flex flex-col items-center justify-center rounded-2xl border border-dashed border-black/[0.14] bg-white px-6 py-12 text-center">
          <span
            className={`grid h-11 w-11 place-items-center rounded-full text-black/45 ${tema.hueco}`}
          >
            <span className="material-symbols-rounded text-[22px]">add_photo_alternate</span>
          </span>
          <p className="mt-3 text-[14.5px] font-semibold">Todavía no generaste nada</p>
          {/* Sin `max-w-sm`: la frase mide 387px en una línea y ese tope son 384 —
              se partía en dos por TRES píxeles
              (ADR-2026-08-04-el-estado-vacio-de-creaciones-en-una-linea). Sin tope,
              usa el ancho que haya: una línea donde entra y dos en un teléfono, que
              es donde el corte tiene sentido. */}
          <p className="mt-1 text-[13px] leading-relaxed text-black/45">
            Elegí un modo de arriba y tu primera creación va a aparecer acá.
          </p>
        </div>
      ) : (
        // 5 creaciones + "Ver más" como sexta celda, igual que las filas de
        // Plantillas (ADR-2026-08-03-ver-mas-en-creaciones-recientes). El hook trae
        // 6 justamente para poder distinguir "hay más" de "esto es todo": si
        // volvieron 6, la sexta no se muestra y su lugar lo ocupa el acceso al
        // historial; con 5 o menos no hay resto que prometer y la celda no aparece.
        <div ref={filaRef} className={`mt-5 ${FILA_CREACIONES}`}>
          {visibles.map((it) => (
            <button
              key={it.id}
              onClick={() => setCreacionAbierta(it.id)}
              // Alto fijo + `aspectRatio` = el ancho lo pone la forma de la imagen.
              // Manda lo medido sobre la miniatura ya cargada; `formato` es la apuesta
              // inicial y 4:5 el respaldo, para que la fila no se reacomode a lo loco
              // mientras cargan.
              style={{ aspectRatio: ratioDe(it) }}
              className={`group relative shrink-0 overflow-hidden rounded-xl border border-black/[0.06] ${ALTO_CREACION} ${tema.tarjeta}`}
            >
              <img
                src={it.src}
                alt={it.alt}
                loading="lazy"
                // Se mide cuando lo declarado no alcanza o no es de fiar: las filas sin
                // `formato` (una de cada cuatro) y TODOS los videos, cuyo formato
                // declarado es el que pidió el flujo y no el del archivo
                // (ADR-2026-08-03-el-formato-de-los-videos-no-es-de-fiar). Para una
                // imagen con `formato` no se mide nada: la caja ya nace con la forma
                // correcta y no hay que esperar la descarga.
                onLoad={
                  it.ratio && !it.esVideo
                    ? undefined
                    : (e) => {
                        const img = e.currentTarget;
                        if (!img.naturalWidth || !img.naturalHeight) return;
                        const medido = img.naturalWidth / img.naturalHeight;
                        setRatiosMedidos((prev) =>
                          prev[it.id] === medido ? prev : { ...prev, [it.id]: medido },
                        );
                      }
                }
                // `object-cover` aunque la caja ya tenga la proporción: `formato` es lo
                // que se PIDIÓ al generar y puede no coincidir al pixel con lo que
                // devolvió el modelo. Sin él, esa diferencia se vería como una franja.
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
              />
              {/* El play va CENTRADO sobre la miniatura y más grande que el 24px que
                  tenía arriba a la izquierda: ahí competía con el contenido de la
                  esquina y a 14px casi no se leía. Centrado es donde se espera el
                  control de un video, y es el único lugar que no depende de qué haya
                  en la imagen. El contenedor se estira a toda la celda y centra con
                  `place-items-center` — así no hace falta un `translate`, que el
                  `:active { scale(.95) }` global de la app pisaría. */}
              {it.esVideo && (
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-11 w-11 place-items-center rounded-full bg-black/50 text-white">
                    <span className="material-symbols-rounded text-[24px]">play_arrow</span>
                  </span>
                </span>
              )}
            </button>
          ))}

          {/* "Ver más" sólo cuando queda algo afuera
              (ADR-2026-08-03-ver-mas-solo-si-hay-algo-mas). Si se ven todas, la fila
              termina donde termina la última miniatura: la celda no tendría nada que
              prometer y estaría ahí sólo para rellenar.

              Cuando sí va, es elástica: se estira para ocupar lo que sobre, así la
              fila cierra justo en el borde del contenedor sea cual sea la mezcla de
              formatos (ADR-2026-08-03-creaciones-entran-las-que-entran). */}
          {mostrarVerMas && (
          <CeldaVerMas
            etiqueta="Ver todas tus creaciones"
            onClick={irAlHistorial}
            tema={tema}
            // Blanco: acá la grilla va sobre el lienzo gris, no dentro de una
            // tarjeta, así que la celda tiene que apoyarse en la misma superficie
            // que las miniaturas que la acompañan.
            fondo={tema.tarjeta}
            // En mobile la fila scrollea, así que ahí conserva un ancho propio (la
            // proporción por defecto) en vez de estirarse: en un contenedor que
            // scrollea, `flex-1` no tiene sobrante que ocupar.
            medidas={`shrink-0 ${ALTO_CREACION} sm:min-w-[132px] sm:flex-1 sm:shrink`}
            estilo={{ aspectRatio: esAngosta ? RATIO_CREACION_DEFECTO : undefined }}
          />
          )}
        </div>
      )}

      {/* Visor de una creación, sin salir de la home
          (ADR-2026-08-03-visor-de-creacion-se-abre-en-la-home). Se monta el historial
          en modo "sólo visor": trae el MISMO visor que esa pantalla, con todas sus
          acciones, y ninguna de su grilla.

          El velo de abajo es el que se ve mientras el historial resuelve su fetch: el
          visor recién puede abrirse cuando la imagen está en su lista. Es clickeable a
          propósito — si esa creación quedara fuera de sus filtros (fuera de los últimos
          30 días, por ejemplo) el visor no abriría nunca, y sin salida el usuario
          quedaría trabado. */}
      {creacionAbierta && (
        <>
          {/* Portaleado a `document.body` — ADR-2026-08-03-el-velo-del-visor-se-portalea.
              Inline, este `fixed` NO se resolvía contra la ventana: cuelga de
              `.aparece-tras-esqueleto`, que se anima con `animation-fill-mode: both` y
              queda con un `transform` de identidad puesto para siempre. Un transform
              —aunque no mueva nada— convierte al elemento en el bloque contenedor de
              sus descendientes `fixed`, así que el velo tomaba el alto y el ancho de la
              home: no tapaba la sidebar y el spinner quedaba centrado en el largo de la
              página, fuera de la vista. El visor de abajo no tiene el problema porque
              ya se portalea. */}
          {createPortal(
            <div
              role="presentation"
              onClick={() => setCreacionAbierta(null)}
              // 300ms contra los 200 del visor: el velo se apaga un poco más lento que
              // lo que tarda el visor en llegar a opaco, así en el cruce nunca queda un
              // hueco por el que se vea la home iluminada.
              // `pointer-events-none` al apagarse: apagado sigue estando ahí, y sin
              // esto se comería el click con el que se cierra el visor.
              className={`fixed inset-0 z-40 grid place-items-center bg-black/70 transition-opacity duration-300 ease-out ${
                visorAbierto
                  ? 'pointer-events-none opacity-0'
                  : veloEncendido
                    ? 'opacity-100'
                    : 'opacity-0'
              }`}
            >
              {/* El spinner entra recién a los 260ms: con una creación que abre rápido
                  —lo normal, porque la miniatura ya está en la cache de la home— un
                  spinner que parpadea 100ms es ruido, no información. */}
              <span style={{ animation: 'page-fade-in 200ms ease-out 260ms both' }}>
                <span className="block h-8 w-8 animate-spin rounded-full border-2 border-white/25 border-t-white" />
              </span>
            </div>,
            document.body,
          )}
          <GeneratedImagesHistory
            soloVisor={{
              imageId: creacionAbierta,
              onCerrar: () => setCreacionAbierta(null),
              onAbierto: () => setVisorAbierto(true),
            }}
          />
        </>
      )}
    </section>
  );
}

/**
 * Con `children` la pantalla deja de ser sólo la home y pasa a hacer de **shell**:
 * conserva sidebar, barra de créditos y panel dev, y en el área de contenido monta
 * lo que le pasen (`/creaciones`, `/editar-marca`). Ver ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo.
 *
 * No se extrajo el shell a un componente aparte a propósito: mover 180 líneas de
 * JSX de un árbol todavía sin commitear (ADR-2026-07-30-queda-solo-fuera-switcher-otras) es riesgo sin ganancia. Cuando
 * se commitee, el corte natural es `ShellHome` + `Home`.
 */
/**
 * El chat como una columna más del shell —
 * ADR-2026-08-20-el-chat-viaja-con-las-pantallas-que-lo-abren.
 *
 * Es un componente aparte y no unas líneas dentro de `EstiloMinimal` por una razón
 * concreta: `useEmpujeChat` lee `sidebarWidth` del `SidebarContext` para calcular el
 * ancho de la franja, y el que vale acá es el del shell (`ctxShell`), no el del
 * provider global. Un hook llamado en el cuerpo de `EstiloMinimal` no puede leer un
 * provider que el propio `EstiloMinimal` renderiza; montado adentro, sí. Así el ancho
 * de la franja y el hueco que le deja el contenido salen del MISMO número, que es la
 * invariante que `rutaChatAtlas.ts` pide en mayúsculas.
 *
 * El hueco es un hermano flex de `<main>` y no un `padding-right`: `<main>` es
 * `flex-1`, así que un espaciador al final de la fila le saca exactamente ese ancho.
 * El panel se dibuja `fixed right-0` con el mismo número, o sea justo encima del
 * espaciador.
 */
function ColumnaChatShell() {
  const { ancho } = useEmpujeChat();
  return (
    <>
      <div
        aria-hidden="true"
        className="shrink-0 transition-[width] duration-300 ease-out"
        style={{ width: ancho }}
      />
      <PanelChatAtlas />
    </>
  );
}

export default function EstiloMinimal({
  variante = 'claro',
}: { variante?: VarianteMinimal } = {}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /**
   * ¿Esta ruta se hospeda en NEUTRO? —
   * ADR-2026-08-20-el-shell-hospeda-en-neutro-lo-que-no-nacio-para-la-columna.
   *
   * `true` para las 13 que se mudaron acá desde `DashboardLayout` por el desktop: se
   * escribieron contra el panel de `.main-content` y hay que dejarlas con su ancho,
   * su padding y sus títulos. Ver el `<Outlet />` al final del archivo.
   */
  const hospedajeNeutro = esRutaShellLegacy(pathname);

  // Fade entre secciones (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El contenido se apaga, recién ahí se navega,
  // y al montar la sección nueva se vuelve a encender. La sidebar y la barra de
  // créditos quedan fuera de este div, así que ni parpadean.
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
  }, [pathname]);
  /**
   * El fade de salida, sin destino: apaga el contenido y corre `accion` cuando la
   * animación terminó. Es la mitad reutilizable de `irA` — la tira de modos de la
   * home necesita esta transición pero NO puede delegar la navegación, porque su
   * destino lo arma `InterfazV2` (ver `conFadeDeSalida` en `CuentaCtx`).
   */
  const conFadeDeSalida = useCallback((accion: () => void) => {
    setVisible(false);
    window.setTimeout(accion, FADE_SALIDA_MS);
  }, []);
  /** Navegación desde la sidebar: apaga, navega, y el efecto de arriba prende. */
  const irA = (ruta: string) => {
    if (ruta === pathname) return;
    conFadeDeSalida(() => navigate(ruta));
  };
  /**
   * Abajo de 1024px la sidebar va SIEMPRE colapsada —
   * ADR-2026-08-05-el-shell-nuevo-arranca-en-768.
   *
   * Desplegada mide 260px con el margen. En un ancho de 800 eso deja 540 para el
   * contenido, y las secciones —pensadas para 1024 en adelante— entran a los golpes: el
   * saludo de la home se parte en tres líneas y la tira de modos se sale. Colapsada mide
   * 82 y quedan 718, que es donde el contenido vuelve a respirar.
   *
   * El toggle se esconde en ese rango en vez de quedar inerte: ofrecer un control que
   * no puede hacer nada es peor que no ofrecerlo. La preferencia guardada no se pisa —
   * se ignora mientras la ventana es angosta y vuelve sola al agrandarla.
   */
  const anchoDeSobra = useIsDesktop();
  const { colapsada: colapsadaPreferida, alternar } = useSidebarColapsable();
  const colapsada = colapsadaPreferida || !anchoDeSobra;
  const tema = TEMAS[variante];

  // La tarjeta de cuenta del pie abre el menú del avatar en vez de navegar al
  // perfil (supersede ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil): es el
  // mismo bloque de identidad que arriba a la derecha, y ahí el click despliega el
  // menú. Llevar a una ruta desde un lado y desplegar del otro hacía que el mismo
  // gesto sobre el mismo dato hiciera dos cosas distintas. "Perfil" sigue estando,
  // ahora como ítem del menú.
  const menuCuentaSidebar = useRef<MenuCuentaHandle>(null);
  const tarjetaCuenta = useRef<HTMLDivElement>(null);

  // Contexto de sidebar RE-PROVISTO para las pantallas hospedadas (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell). Se
  // hereda el del provider real —así los setters del header mobile siguen llegando
  // a quien corresponde— y sólo se pisa la geometría, que es lo único que este
  // shell tiene distinto: su sidebar está en flujo, mide otra cosa y colapsa con su
  // propio estado. Sin esto, un panel `fixed` anclado a `sidebarWidth` se posiciona
  // contra una sidebar que en esta pantalla no existe.
  const ctxHeredado = useSidebar();
  const ctxShell = useMemo(
    () => ({
      ...ctxHeredado,
      isCollapsed: colapsada,
      sidebarWidth:
        (colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR) + MARGEN_SIDEBAR * 2,
    }),
    [ctxHeredado, colapsada],
  );
  // Descarte sólo en memoria, a propósito: la barra avisa un problema real (saldo
  // en cero o por debajo del umbral) que no se resuelve cerrándola. Persistirlo
  // dejaría al usuario sin el aviso justo cuando le empieza a importar; se va por
  // lo que dura la sesión y vuelve al recargar.
  const [barraDescartada, setBarraDescartada] = useState(false);

  /**
   * Alto ocupado por la barra de créditos, publicado como variable CSS
   * (`--shell-barra-creditos`) sobre la raíz del shell —
   * ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra.
   *
   * Las pantallas de modo se montan como un panel `fixed inset-y-0`, así que TAPABAN
   * la barra: el aviso de saldo desaparecía apenas se entraba a un modo, que es
   * justo cuando importa. En vez de montar una segunda barra adentro del panel
   * —dos instancias, dos estados de descarte—, el panel arranca DEBAJO de ésta.
   *
   * Se mide el `offsetHeight` de la franja y NADA más. Antes era
   * `offsetTop + offsetHeight`, que con la barra en el flujo daba lo mismo —los 10px
   * de `offsetTop` eran el aire de arriba— pero desde que la franja es `sticky`
   * (ADR-2026-08-04-la-barra-de-creditos-no-scrollea) `offsetTop` crece con el scroll:
   * la caja pegada se re-posiciona, y los paneles se habrían ido bajando solos al
   * scrollear. El aire de arriba pasó a ser padding DE la franja, así que su alto ya
   * incluye todo lo que ocupa; y como `<main>` arranca en el tope de la ventana, ese
   * alto ES la coordenada donde termina la barra.
   */
  const barraCreditos = useRef<HTMLDivElement>(null);
  const [altoBarraCreditos, setAltoBarraCreditos] = useState(0);

  // Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). El estado vive en el SHELL y no en
  // la sección: los disparadores están repartidos entre los dos —la tarjeta de la
  // sidebar y la barra acá, el botón del encabezado en `HomeMinimal`— y si viviera
  // en la sección, el overlay se cerraría al cambiar de pantalla.
  const [motivoPricing, setMotivoPricing] = useState<MotivoPricing | null>(null);
  const abrirPricing = useCallback((motivo: MotivoPricing = 'mejorar') => {
    setMotivoPricing(motivo);
  }, []);

  // Marca activa REAL — misma fuente y mismo orden de preferencia que el editor
  // de marca (`config_sections/CompanyData`): el rubro de la marca activa
  // (`brand_profiles.rubro`) y, si no tiene, el de la empresa
  // (`company_profiles.rubro`). Antes era un ejemplo fijo, y el rubro del selector
  // podía no coincidir con el declarado en "Editar marca" (ADR-2026-07-31-rubro-selector-sale-marca-real).
  const {
    profile: perfilMarca,
    activeBrand: marcaActiva,
    loading: cargandoMarca,
    // Para el menú de marcas de la tarjeta: la lista, cuál está activa y el cambio.
    brands: marcas,
    currentBrandId: marcaActivaId,
    switchBrand: cambiarMarca,
    switching: cambiandoMarca,
  } = useBrandProfile();
  const marca = {
    nombre: marcaActiva?.name ?? perfilMarca?.company_name ?? MARCA_EJEMPLO.nombre,
    rubro: marcaActiva?.rubro ?? perfilMarca?.rubro ?? MARCA_EJEMPLO.rubro,
    // Logo de la marca ACTIVA — ADR-2026-08-04-la-marca-se-ve-con-su-logo. El
    // `company_logo` no es un fallback de "esta marca no tiene logo" sino de "esta
    // cuenta no tiene marcas": si hay marca activa y no tiene logo, va la inicial.
    // Caer a la empresa mostraría el logo de la marca vieja en las marcas nuevas —
    // es el mismo criterio que ya aplica el Sidebar de producción.
    logo: marcaActiva ? marcaActiva.logo_url : (perfilMarca?.company_logo ?? null),
  };

  // Cuenta REAL — ADR-2026-07-31-shell-nuevo-datos-reales. Identidad de `profiles`, plan/saldo/capacidad
  // de `company_profiles` y el descuento del mismo cupón invisible que aplica el
  // catálogo. Antes eran las constantes `PLAN_DEFAULT` / `CREDITOS_DEFAULT` /
  // `DESCUENTO_DEFAULT`, que servían mientras esto era un preview sin sesión.
  const cuentaReal = useCuentaAtlas();

  /**
   * Los guards que `DashboardLayout` corre además de dibujar su chrome —
   * ADR-2026-08-05-el-shell-nuevo-si-corre-los-guards.
   *
   * `useShellGuards` se creó justamente para que los DOS shells corrieran lo mismo
   * (ADR-2026-07-31-shell-nuevo-urls-viejas), pero quedó llamado sólo desde
   * `DashboardLayout`: en desktop, las diez rutas del rediseño no lo ejecutaban.
   *
   * Lo que faltaba correr ahí:
   *  - El **guard de marca en borrador**. Una cuenta con el alta a medias entraba a la
   *    app y todas las queries filtran `is_draft = false`, así que veía la home vacía
   *    sin ninguna explicación en vez de volver a terminar su marca.
   *  - El **reset de `overflow`** en cada cambio de ruta, que destraba el scroll global
   *    cuando un overlay se cierra mal.
   */
  useShellGuards();

  /* Acá se aplicaba encima el contexto simulado del panel CONTEXTO DEV
     (ADR-2026-07-30-panel-contexto-dev-muda-esa): `dev.plan ?? cuentaReal.plan`, ídem
     créditos, descuento y capacidad. El panel y sus overrides se eliminaron al mergear
     `principal` (ADR-2026-08-05-fuera-los-overrides-de-dev), así que el shell muestra
     siempre la cuenta de verdad. */
  const plan = cuentaReal.plan;

  /**
   * La lista de herramientas ordenada según el plan: primero lo que se puede usar,
   * después Plantillas cerrando ese bloque, y al fondo lo bloqueado
   * (ADR-2026-08-03-plantillas-cierra-los-disponibles).
   *
   * El criterio de bloqueo es el MISMO que decide el candado y el click —
   * `getModePlanLock` sobre el plan real—, pasado como función para no repetirlo:
   * el orden, el ícono y el destino no pueden discrepar sobre la misma herramienta.
   */
  const herramientasOrdenadas = useMemo(
    () =>
      ordenarHerramientas(
        // Plantillas sólo para Moda (ADR-2026-08-03-plantillas-solo-para-moda). Se
        // FILTRA, no se bloquea: un candado ofrece algo que se destraba pagando, y
        // acá no hay nada que destrabar — a ese rubro el catálogo no le corresponde.
        HERRAMIENTAS_SIDEBAR.filter((h) => {
          if (h.id === 'plantillas') return rubroVePlantillas(marca.rubro);
          // El resto sale de los FLAGS del catálogo y ya no de una lista de `id`
          // escrita a mano — ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
          //
          // Acá estaban repetidos, por nombre, los mismos ejes que el catálogo ya
          // declara: `infografia`/`catalogo-muebles` son `soloHogarDeco`, y
          // `modelo`/`catalogo` son `ocultoEnHogarDeco`. Con la lista escrita a mano,
          // un modo nuevo con esos flags quedaba visible acá aunque el resto de la app
          // lo escondiera. Y faltaba `devOnly` por completo: ésta era la ÚNICA de las
          // cuatro superficies que listaba los modos internos a cualquiera.
          //
          // `HERRAMIENTAS_EXTRA` (Estudio de Video) no trae flags → pasa, que es lo
          // que corresponde: su gating es por plan, más abajo.
          return modoVisiblePorFlags(h, {
            isDevTester: cuentaReal.devTester,
            esHogarDeco: esRubroHogarDeco(marca.rubro),
          });
        }),
        (h) => !!getModePlanLock(MODE_PATH_TO_KIND[h.id] ?? h.id, plan),
      ),
    [plan, marca.rubro, cuentaReal.devTester],
  );

  /**
   * Accesos internos visibles — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
   *
   * Mismo orden de chequeos que el filtro de `components/Sidebar.tsx`: primero el
   * permiso, después `dev_tester`. Importa que sea ese orden y no al revés, porque las
   * claves `em-*` y `chat-atlas` están en `true` por defecto JUSTAMENTE para que el
   * corte lo dé `devOnly` y no el permiso (ver los comentarios de
   * `PermissionContext`); invertirlo cambiaría a quién le aparecen.
   *
   * Hoy `PermissionContext` devuelve SIEMPRE `defaultPermissions` —la tabla
   * `config_users` ya no existe y el fetch lo dice en un comentario—, así que este
   * chequeo es inerte y quien manda de verdad es `dev_tester`. Se consulta igual, y no
   * se simplifica a `devTester` a secas, para que el día que vuelva a haber permisos
   * por cuenta esta sidebar los respete sin que haya que acordarse de ella.
   */
  const { permissions } = usePermissions();
  const accesosInternos = useMemo(
    () =>
      cuentaReal.devTester
        ? ACCESOS_INTERNOS.filter((a) => permissions[a.permiso])
        : [],
    [permissions, cuentaReal.devTester],
  );

  /**
   * La nav de arriba, filtrada por permiso. Ver el comentario de `NAV_LATERAL` para
   * por qué "Inicio" no se gatea por grupo de testeo.
   */
  const navLateralVisible = useMemo(
    () => NAV_LATERAL.filter((item) => !('permiso' in item) || permissions[item.permiso]),
    [permissions],
  );

  const creditos = cuentaReal.creditos;
  const descuento = cuentaReal.descuento;
  const planLabel = ETIQUETA_PLAN[plan] ?? plan;
  // Mismo criterio que la app real (ADR-2026-07-30-solo-criterio-saldo-toda-app): sin
  // créditos, o por debajo del 5% de la capacidad del plan. Vale para cualquier plan.
  //
  // La capacidad sale de `company_profiles.max_fotos_mes` cuando la fila la trae, y
  // sólo cae a `CAPACIDAD_POR_PLAN` si no: esa tabla es una aproximación por plan y a
  // un cliente con cupo a medida le movería el umbral del aviso.
  const capacidad = cuentaReal.capacidad ?? CAPACIDAD_POR_PLAN[plan];
  const estadoCreditos = estadoDeCreditos(creditos, capacidad);
  const estadoBarra: 'sin' | 'pocos' =
    estadoCreditos === 'ok' ? 'sin' : estadoCreditos;
  // Mientras la cuenta carga, `creditos` vale 0 y el estado daría "sin créditos":
  // la barra roja aparecería en cada entrada al shell y se iría sola un instante
  // después. Se espera a tener el dato.
  const mostrarBarra =
    !barraDescartada && !cuentaReal.cargando && estadoCreditos !== 'ok';
  // La medición va acá y no junto al `useRef`: depende de `mostrarBarra`, que se
  // resuelve recién en esta línea.
  useEffect(() => {
    const el = barraCreditos.current;
    if (!el) {
      setAltoBarraCreditos(0);
      return;
    }
    const medir = () => setAltoBarraCreditos(el.offsetHeight);
    medir();
    // El alto cambia sin desmontar: el copy de la barra se reacomoda en dos líneas
    // cuando la ventana se angosta (o cuando la sidebar se despliega).
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [mostrarBarra]);  // Criterio del CTA de mejora, una sola vez para los dos botones que lo usan.
  const planAlDia = plan !== 'gratis' && estadoCreditos === 'ok';

  /**
   * Cambiar de marca remonta la pantalla hospedada —
   * ADR-2026-08-03-cambiar-de-marca-remonta-la-pantalla.
   *
   * Cada pantalla resolvía por su cuenta qué hacer con `atlas:brand-changed`, y la
   * mayoría no hacía nada: la marca cambiaba en la sidebar y a la derecha seguía la
   * información de la anterior hasta navegar a otro lado y volver. En vez de sumarle
   * un listener a cada una, el shell le cambia la `key` al contenido: la pantalla se
   * desmonta y se monta de nuevo, así que vuelve a correr TODOS sus fetch de montaje
   * con la marca nueva, sepa o no del evento.
   *
   * El `nonce` no es `marcaActivaId`: al arrancar, ese valor pasa de `null` al id
   * real cuando resuelve el fetch de marcas, y usarlo de `key` remontaría la pantalla
   * una vez en cada carga —doble fetch de todo, y un parpadeo—. Se cuentan CAMBIOS,
   * no valores: la primera resolución sólo se anota.
   *
   * Va con el mismo fade que un cambio de sección (`FADE_SALIDA_MS`), y por eso el
   * remonte espera a que la salida termine: sin eso el contenido nuevo aparece de
   * golpe encima del viejo.
   */
  const marcaPrevia = useRef<string | null>(null);
  const [nonceMarca, setNonceMarca] = useState(0);
  useEffect(() => {
    if (!marcaActivaId) return;
    if (marcaPrevia.current === null) {
      marcaPrevia.current = marcaActivaId;
      return;
    }
    if (marcaPrevia.current === marcaActivaId) return;
    marcaPrevia.current = marcaActivaId;
    setVisible(false);
    const id = window.setTimeout(() => {
      setNonceMarca((n) => n + 1);
      setVisible(true);
    }, FADE_SALIDA_MS);
    return () => window.clearTimeout(id);
  }, [marcaActivaId]);

  const inicial = inicialDe(cuentaReal.nombreCompleto, cuentaReal.email);

  // Memo para no rehacer el objeto —y con él re-renderizar a todos los
  // consumidores— en cada render del shell.
  const cuenta = useMemo<CuentaCtx>(
    () => ({
      abrirPricing,
      creditos,
      planLabel,
      planAlDia,
      nombreCompleto: cuentaReal.nombreCompleto,
      email: cuentaReal.email,
      inicial,
      idCompany: cuentaReal.idCompany,
      // Sólo en cuentas de 2+ marcas (ADR-2026-08-03-creaciones-recientes-por-marca):
      // con una sola marca, filtrar por ella no distingue nada y sí esconde lo que el
      // join no puede resolver (generaciones sin producto, o con un producto sin
      // `brand_id`). Es la misma condición con la que el historial muestra su
      // dropdown de marcas.
      idMarca: marcas.length > 1 ? marcaActivaId : null,
      // El rubro va SIEMPRE, sin la condición de 2+ marcas de `idMarca`: eso de arriba
      // es para no filtrar de más en creaciones recientes, y acá lo que se decide es
      // con qué foto se ve un modo, que aplica igual con una sola marca.
      rubroMarca: marca.rubro ?? null,
      // Literal: si este provider está en el árbol, el shell es quien monta.
      dentroDelShell: true,
      conFadeDeSalida,
      cargandoCuenta: cuentaReal.cargando,
    }),
    [
      abrirPricing,
      creditos,
      planLabel,
      planAlDia,
      cuentaReal.nombreCompleto,
      cuentaReal.email,
      inicial,
      cuentaReal.idCompany,
      cuentaReal.cargando,
      marcaActivaId,
      marcas.length,
      marca.rubro,
      conFadeDeSalida,
    ],
  );

  return (
    // El provider envuelve TODO el shell —no sólo el `<Outlet />`— para que los
    // disparadores del overlay puedan estar de los dos lados: hoy los tres de la
    // sidebar y el del encabezado de la home (ADR-2026-07-31-creditos-mejora-plan-encabezado-home).
    <CuentaContext.Provider value={cuenta}>
    {/* `estilos-cursor-scope`: manito custom de la app sobre lo clickeable (ADR-2026-07-30-minimal-plantillas-reales-secciones-misma).
        Sin la clase, el cursor custom del <body> pinta la MISMA flecha sobre botones
        y fondo, y nada se siente clickeable. */}
    <div
      // `--shell-barra-creditos`: dónde termina la barra de créditos. La leen los
      // paneles de modo, que se montan `fixed` adentro de este árbol y arrancan
      // debajo de ella (ADR-2026-08-03-paneles-de-modo-arrancan-bajo-la-barra).
      // Vale 0 cuando la barra no está.
      style={
        {
          '--shell-barra-creditos': `${altoBarraCreditos}px`,
        } as CSSProperties
      }
      className={`estilos-cursor-scope flex h-screen overflow-hidden font-sans text-[#16181D] ${tema.fondo}`}
    >
      {/* ── Sidebar flotante ────────────────────────────────────────────── */}
      <aside
        style={{ width: colapsada ? ANCHO_SIDEBAR_COLAPSADA : ANCHO_SIDEBAR }}
        className={`m-2 flex shrink-0 flex-col overflow-hidden rounded-2xl border border-black/[0.06] px-3 py-4 transition-[width] duration-300 ease-out ${tema.panel}`}
      >
        <div
          className={`mb-5 flex items-center px-1.5 ${colapsada ? 'justify-center' : 'justify-between'}`}
        >
          {/* Expandida va el lockup horizontal (isotipo + marca); colapsada no hay
              ancho para la palabra y sólo queda el botón de expandir. */}
          {!colapsada && (
            <img
              src="/images/estudio-atlas-logo-horizontal.svg"
              alt="Estudio Atlas"
              className="h-[19px] w-auto"
            />
          )}
          {/* El toggle sólo donde hay ancho para expandir: abajo de 1024 la sidebar va
              colapsada sí o sí (ver `colapsada`, arriba). */}
          {anchoDeSobra && (
            <button
              onClick={alternar}
              aria-label={colapsada ? 'Expandir menú' : 'Colapsar menú'}
              title={colapsada ? 'Expandir menú' : 'Colapsar menú'}
              className="grid h-7 w-7 place-items-center rounded-lg text-black/30 transition-colors hover:bg-black/[0.04] hover:text-black/60"
            >
              <span className="material-symbols-rounded text-[19px]">
                {colapsada ? 'left_panel_open' : 'left_panel_close'}
              </span>
            </button>
          )}
        </div>

        {/* La tarjeta despliega el menú de marcas en vez de ir a `/mi-marca`
            (ADR-2026-08-03-tarjeta-marca-abre-menu-de-marcas). El `unfold_more` que ya
            tenía prometía justamente esto. "Editar marca actual", adentro del menú,
            conserva el destino viejo — ahora abierto en "Información"
            (ADR-2026-08-05-editar-marca-abre-informacion), que es donde se editan los
            datos de la marca. La marca es la activa: `/mi-marca` trabaja siempre sobre
            ella, así que no hay nada que pasarle. */}
        <MenuMarcas
          marcas={marcas}
          marcaActivaId={marcaActivaId}
          cambiarMarca={cambiarMarca}
          cambiando={cambiandoMarca}
          onEditarMarca={() => irA('/mi-marca?tab=informacion')}
        >
        <span
          title={colapsada ? `${marca.nombre} — ${marca.rubro}` : undefined}
          className={`mb-4 flex items-center rounded-xl py-2 text-left transition-colors hover:bg-black/[0.03] ${
            colapsada ? 'justify-center px-0' : 'gap-2.5 px-2'
          }`}
        >
          {/* 35px = alto exacto de la columna de al lado (18 del nombre + 2 del gap
              + 15 del rubro), para que el avatar arranque y termine con el bloque de
              texto — ADR-2026-07-31-tarjeta-marca-sidebar-alturas-atadas.
              `rounded-xl` (12px) y no `rounded-full`: el shell pasó a radio de 12px
              en avatares y pastillas, así que este sigue al resto. Ojo con `lg`, que
              en este config son 16px: a 35px de lado se ve casi igual de redondo.

              Mientras carga va el bloque gris y no la inicial, por lo mismo que los
              dos textos de al lado: la inicial que habría es la de la marca de
              ejemplo, y verla saltar a otra letra —o al logo— es peor que no ver
              nada (ADR-2026-08-04-la-marca-se-ve-con-su-logo). */}
          {cargandoMarca ? (
            <span className="h-[35px] w-[35px] shrink-0 rounded-xl bg-black/[0.07]" />
          ) : (
            <AvatarMarca
              logo={marca.logo}
              nombre={marca.nombre}
              className="h-[35px] w-[35px] shrink-0 rounded-xl"
              claseInicial="text-[12px]"
            />
          )}
          {!colapsada && (
            <>
              {/* Nombre y rubro apilados. `min-w-0` en la columna: sin eso el
                  `truncate` de los hijos no corta —un item de flex no baja de su
                  contenido— y el chevron se sale de la sidebar con nombres largos.

                  Mientras carga van dos barras grises en lugar de los textos: sin
                  eso se ve la marca de ejemplo y un instante después salta a la
                  real, que es peor que no mostrar nada. Miden lo mismo que las dos
                  líneas, así que el alto de la sidebar no se mueve. */}
              <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
                {cargandoMarca ? (
                  <>
                    {/* Los márgenes verticales llevan cada barra al alto de la línea
                        que reemplaza (12+3+3=18 y 10+2,5+2,5=15) para que el bloque
                        mida los mismos 35px cargando y cargado. */}
                    <span className="my-[3px] h-3 w-24 rounded bg-black/[0.07]" />
                    <span className="my-[2.5px] h-2.5 w-16 rounded bg-black/[0.05]" />
                  </>
                ) : (
                  <>
                    <span className="truncate text-[13.5px] font-[500] leading-[18px]">
                      {marca.nombre}
                    </span>
                    <span className="truncate text-[11.5px] font-[400] leading-[15px] text-black/45">
                      {marca.rubro}
                    </span>
                  </>
                )}
              </span>
              <span className="material-symbols-rounded text-[16px] text-black/30">unfold_more</span>
            </>
          )}
        </span>
        </MenuMarcas>

        {/* `gap-1.5` = 6px entre ítems (ADR-2026-07-31-columna-contenido-90-mejorar-plan). */}
        <nav className="flex flex-col gap-1.5">
          {navLateralVisible.map((item) => (
            <button
              key={item.label}
              onClick={() => irA(item.ruta)}
              title={colapsada ? item.label : undefined}
              className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
              } ${
                // Seleccionado: ícono y texto en el azul de marca, con el fondo que
                // le corresponda a la variante (tinte azul en clara, blanco en gris).
                // Sale de la ruta actual y ya no de `i === 0`: con la nav navegando
                // de verdad entre tres pantallas (ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo), fijarlo en el primero
                // dejaba "Inicio" marcado estando en Creaciones.
                item.ruta === pathname
                  ? `${tema.seleccion} ${ITEM_ACTIVO}`
                  : `${GRIS_SHELL} hover:bg-black/[0.03]`
              }`}
            >
              {/* Sin `opacity-75` propia: el ícono toma el mismo negro al 50% que la
                  etiqueta. Con la opacidad encima quedaba al 37,5% y los íconos se
                  leían más apagados que su texto. */}
              <span className="material-symbols-rounded shrink-0 text-[19px]">
                {item.icono}
              </span>
              {!colapsada && item.label}
            </button>
          ))}
        </nav>

        {/* Divisor: 16px arriba, 22px abajo, al 80% del ancho y alineado a la
            izquierda (ADR-2026-07-31-divisor-sidebar-16-22-aire). Sin `mx-auto` el flex column lo deja arrancando en
            el borde izquierdo del contenido, que ya trae el `px-3` de la sidebar. */}
        <div className="mb-[22px] ml-3 mt-4 h-px w-4/5 bg-black/[0.06]" />

        {/* Herramientas: el catálogo completo, con las rutas reales de cada modo.
            La lista sale de `contenidoAtlas`, así que sigue a `MODES` sin tocar
            esta pantalla; los nombres salen de `etiquetaSidebar`, que los toma del
            carrusel para que un modo se llame igual en los dos lados (ADR-2026-07-31-solo-nombre-modo-sidebar-toma). */}
        {/* `gap-1.5` = 6px, el MISMO que la nav de arriba (ADR-2026-07-31-columna-contenido-90-mejorar-plan): las dos listas
            son ítems de navegación del mismo peso, y separarlas distinto hacía que
            la de herramientas se leyera como un bloque más apretado. */}
        <nav className="flex flex-col gap-1.5 overflow-y-auto">
          {/* Rótulo del grupo, no un ítem: sin ícono, sin click y más chico que la
              navegación. Colapsada no hay ancho para la palabra y el divisor de
              arriba ya alcanza para separar. */}
          {!colapsada && (
            <p className="mb-2 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
              Todas las herramientas
            </p>
          )}
          {herramientasOrdenadas.map((h) => {
            const destino = rutaSidebar(h);
            // Las herramientas ahora son secciones de este shell (ADR-2026-07-31-8-herramientas-sidebar-hospedan-shell), así que
            // se marcan y se cruzan con el mismo fade que Inicio/Creaciones/Editar
            // marca. `startsWith` y no `===` porque el modo abre sub-rutas.
            const activa = pathname === destino || pathname.startsWith(`${destino}/`);
            // Candado en lugar del ícono cuando el plan no incluye la herramienta
            // (ADR-2026-08-03-sidebar-candado-modos-sin-plan). Mismo criterio que la
            // tira de la home —`getModePlanLock`, sobre el mismo plan— y no una regla
            // propia: la lista y las cards tienen que decir lo mismo de lo mismo.
            //
            // El `?? h.id` es lo que suma "Estudio de Video"
            // (ADR-2026-08-03-estudio-de-video-tambien-con-candado). `MODE_PATH_TO_KIND`
            // traduce el PATH de un modo a su `kind`, y ahí sólo están los ocho de
            // `MODES`; el estudio no es un modo, así que caía en `undefined` y
            // `getModePlanLock` devolvía `null` — sin candado, aunque la propia función
            // tiene una regla para él (pide Ultra). Su id ya ES el kind que esa función
            // espera, así que alcanza con usarlo cuando el mapa no lo conoce.
            //
            // "Plantillas" pasa por acá igual y sigue sin candado: no tiene regla de
            // plan, así que `getModePlanLock` devuelve `null` para ella.
            const bloqueada = !!getModePlanLock(MODE_PATH_TO_KIND[h.id] ?? h.id, plan);
            return (
              <button
                key={h.id}
                // Con candado no se navega: se abre el catálogo de planes
                // (ADR-2026-08-03-candado-de-sidebar-abre-el-catalogo). Antes el ítem
                // llevaba igual al modo y era la propia pantalla la que lo rebotaba
                // —abría `SubscriptionLimitModal` y volvía a la home—, así que el
                // usuario iba y venía para terminar en un cartel distinto del que
                // muestran los otros accesos bloqueados de la app.
                //
                // El motivo es `'bloqueado'`, que ya existía y trae el copy de
                // "Desbloqueá todo el potencial de Estudio Atlas": el momento es ese
                // —le mostraron que hay más Atlas del que puede usar— y no tiene nada
                // que ver con el saldo, que es lo que separa este motivo de
                // `sin`/`pocos`.
                onClick={() => (bloqueada ? abrirPricing('bloqueado') : irA(destino))}
                title={
                  colapsada
                    ? `${etiquetaSidebar(h)}${bloqueada ? ' — incluido en los planes pagos' : ''}`
                    : undefined
                }
                className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                  colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                } ${
                  activa
                    ? `${tema.seleccion} ${ITEM_ACTIVO}`
                    : bloqueada
                      ? `${GRIS_BLOQUEADO} hover:bg-black/[0.03]`
                      : `${GRIS_SHELL} hover:bg-black/[0.03]`
                }`}
              >
                <span className="material-symbols-rounded shrink-0 text-[19px]">
                  {bloqueada ? 'lock' : h.icono}
                </span>
                {!colapsada && <span className="truncate">{etiquetaSidebar(h)}</span>}
              </button>
            );
          })}

          {/* Accesos internos (`dev_tester`) — ADR-2026-08-20-la-sidebar-nueva-no-miraba-permisos.
              Van DENTRO de esta misma `<nav>` y no en una propia para que scrolleen
              con las herramientas: son la continuación de la lista, y una segunda
              caja con su propio scroll parte el recorrido en dos.
              `accesosInternos` ya viene vacío para quien no es dev tester, así que
              para un cliente esto no dibuja ni un nodo. */}
          {accesosInternos.map((a) => {
            const activa =
              pathname === a.ruta ||
              pathname.startsWith(`${a.ruta}/`) ||
              (a.rutasActivas?.some(
                (r) => pathname === r || pathname.startsWith(`${r}/`),
              ) ??
                false);
            return (
              <Fragment key={a.ruta}>
                {/* Mismo rótulo que "Todas las herramientas": es un encabezado de
                    grupo, no un ítem. Colapsada no hay ancho para la palabra. */}
                {a.abreGrupo && !colapsada && (
                  <p className="mb-2 mt-3 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-black/35">
                    {a.abreGrupo}
                  </p>
                )}
                <button
                  onClick={() => irA(a.ruta)}
                  title={colapsada ? a.label : undefined}
                  className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors ${
                    colapsada ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-2.5'
                  } ${
                    activa
                      ? `${tema.seleccion} ${ITEM_ACTIVO}`
                      : `${GRIS_SHELL} hover:bg-black/[0.03]`
                  }`}
                >
                  <span className="material-symbols-rounded shrink-0 text-[19px]">
                    {a.icono}
                  </span>
                  {!colapsada && <span className="truncate">{a.label}</span>}
                </button>
              </Fragment>
            );
          })}
        </nav>

        {/* Pie: soporte y, cerrando la sidebar, la tarjeta de plan + créditos.
            El perfil no va como ítem suelto: es el botón de usuario de la tarjeta. */}
        <div className="mt-auto pt-4">
          {/* Soporte: ícono genérico de ayuda, no el logo de WhatsApp — el canal
              es un detalle del destino, no la etiqueta. Va a `/ayuda`, la pantalla
              de ayuda de la app; el contacto directo por WhatsApp es su propio
              control ("Whatsapp") en el encabezado. */}
          <button
            type="button"
            // `irA` y no `navigate`: desde que `/ayuda` se monta en este shell
            // (ADR-2026-08-04-ayuda-entra-al-shell-nuevo) es una sección más, y
            // tiene que entrar con el mismo fade que las de arriba. Con `navigate`
            // era el único ítem de la sidebar que cambiaba de pantalla de golpe.
            onClick={() => irA('/ayuda')}
            title={colapsada ? 'Centro de ayuda' : undefined}
            className={`flex items-center rounded-xl py-2 text-left text-[13.5px] font-[500] transition-colors hover:bg-black/[0.03] hover:text-black/85 ${GRIS_SHELL} ${
              colapsada ? 'mx-auto h-9 w-9 justify-center' : 'w-full gap-2.5 px-2.5'
            }`}
          >
            <span className="material-symbols-rounded shrink-0 text-[19px]">support_agent</span>
            {!colapsada && 'Centro de ayuda'}
          </button>

          {/* Tarjeta de cuenta: plan vigente, créditos restantes y el CTA de mejora.
              El contador usa `token_icon.png`, el ícono de créditos oficial de la app
              (el mismo del Sidebar real y de `CreditBadge`). */}
          {colapsada ? (
            <div className="mt-2 flex flex-col items-center gap-1.5">
              <MenuCuenta variante="sidebar">
                <AvatarUsuario />
              </MenuCuenta>
              <button
                onClick={() => abrirPricing('mejorar')}
                title="Mejorar plan"
                aria-label="Mejorar plan"
                className="grid h-9 w-9 place-items-center rounded-xl bg-brand-primary text-white transition-colors hover:bg-brand-primary-hover"
              >
                <span className="material-symbols-rounded text-[19px]">bolt</span>
              </button>
            </div>
          ) : (
            // Blanca en las dos variantes: en la clara se separa del panel por el
            // borde, y en la gris es justamente la figura sobre el panel gris.
            //
            // La tarjeta ENTERA despliega el menú de cuenta, el mismo que el avatar
            // de arriba a la derecha. Es un `div` y no un `button` porque adentro hay
            // otros dos controles —el avatar y "Mejorar plan"—, y anidar botones es
            // HTML inválido; por eso dispara el menú por handle en vez de envolverlo.
            //
            // No lleva `role="button"` ni `tabIndex`: un control que contiene otros
            // controles se anuncia mal y atrapa el foco. El click de la tarjeta es una
            // comodidad de puntero; el acceso por teclado y para lectores de pantalla
            // sigue siendo el avatar, que sí es un botón con `aria-haspopup`.
            <div
              ref={tarjetaCuenta}
              onClick={() => menuCuentaSidebar.current?.alternar()}
              className="mt-2 cursor-pointer rounded-2xl border border-black/[0.09] bg-white p-3 transition-colors hover:border-black/[0.14]"
            >
              <div className="flex items-center gap-2.5">
                {/* `contents`: el wrapper existe SÓLO para frenar la propagación, así
                    que no participa del layout y la fila queda igual que antes. */}
                <span className="contents" onClick={(e) => e.stopPropagation()}>
                  <MenuCuenta
                    ref={menuCuentaSidebar}
                    variante="sidebar"
                    zona={tarjetaCuenta}
                  >
                    <AvatarUsuario />
                  </MenuCuenta>
                </span>
                {/* Plan y saldo un escalón por debajo de la navegación (ADR-2026-07-31-plan-saldo-escalon-debajo-tarjeta): son
                    el estado de la cuenta, no una acción. El que manda en la tarjeta es
                    el botón de abajo. */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12.5px] font-semibold leading-tight">
                    {planLabel}
                  </p>
                  <p className="mt-1 flex items-center gap-1 text-[12px] font-semibold leading-none text-brand-primary">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="h-3 w-3"
                    />
                    <span className="tabular-nums">{creditos}</span>
                  </p>
                </div>
                {/* Acá iba un botón de perfil (ADR-2026-07-31-engranaje-tarjeta-cuenta-pasa-icono). Se sacó en ADR-2026-07-31-fuera-boton-perfil-tarjeta-cuenta: desde
                    que la tarjeta ENTERA lleva al perfil (ADR-2026-07-31-tarjeta-cuenta-entera-lleva-perfil), era un tercer
                    control apretado en la misma fila para el mismo destino.
                    El acceso por teclado no se pierde: el ítem "Perfil" del menú que
                    abre el avatar de la izquierda va al mismo lado. */}
                {/* El mismo `unfold_more` que la tarjeta de marca: desde que la tarjeta
                    despliega un menú (y ya no navega), nada lo anunciaba y se leía como
                    un bloque de información. No es un botón —el control es la tarjeta
                    entera—, así que va como ícono suelto. */}
                <span
                  aria-hidden="true"
                  className="material-symbols-rounded shrink-0 text-[16px] text-black/30"
                >
                  unfold_more
                </span>
              </div>
              {/* Sólido sólo cuando hay algo que resolver: plan gratis, o saldo en
                  cero / por debajo del umbral (ADR-2026-07-31-columna-contenido-90-mejorar-plan). El criterio vive en
                  `clasesMejorarPlan`, compartido con el CTA del encabezado. */}
              <button
                onClick={(e) => {
                  // Sin esto el click sube a la tarjeta y despliega el menú de cuenta
                  // en vez de abrir el catálogo.
                  e.stopPropagation();
                  abrirPricing('mejorar');
                }}
                className={`mt-3 w-full rounded-xl py-2.5 text-[12.5px] font-semibold transition-colors ${clasesMejorarPlan(planAlDia)}`}
              >
                Mejorar plan
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Contenido ─────────────────────────────────────────────────────
          `content-panel-clip` NO tiene CSS: es el hook por el que
          `utils/appScroll` y `ScrollToTop` encuentran el scroller de la app
          (ADR-171). Poniéndoselo acá, las pantallas que se montan adentro
          conservan su guardado/restaurado de scroll y el reset al cambiar de
          ruta sin tocarles una línea. `shell-home-scroll` reinterpreta sus
          `min-h-screen` (ver index.css). Ambos son ADR-2026-07-30-pantallas-siempre-dentro-shell-nuevo. */}
      <main className="relative flex-1 overflow-y-auto content-panel-clip shell-home-scroll">
        {/* La barra de créditos vive en el SHELL, arriba del contenido: es del
            usuario, no de la sección, así que no se va ni reaparece al cambiar de
            pantalla.
            Va en la MISMA columna que el contenido de las pantallas: el 90% centrado
            del wrapper de abajo más sus 32px de sangría
            (ADR-2026-08-03-barra-creditos-vuelve-a-la-columna, que supersede
            ADR-2026-08-03-barra-creditos-margenes-fijos-32 y restituye
            ADR-2026-07-30-barra-creditos-alinea-columna-contenido). Los 32px pelados
            se eligieron cuando las pantallas hospedadas ponían su propia sangría
            fija; desde ADR-2026-08-03-sangria-32-la-pone-el-shell la sangría es de
            este wrapper, o sea 5% + 32px, y la barra era lo único que no la seguía.
            El `mx-auto max-w-[90%]` es el del wrapper y el `px-8` su sangría: los dos
            juntos, no uno u otro. */}
        {mostrarBarra && (
          // `sticky top-0` — ADR-2026-08-04-la-barra-de-creditos-no-scrollea. La barra
          // vivía en el flujo de `<main>`, que ES el scroller de la app: al bajar se
          // iba con el contenido, y el aviso de saldo desaparecía justo cuando el
          // usuario estaba trabajando. Ahora se queda arriba de todo.
          //
          // La franja va a ANCHO COMPLETO y la columna queda adentro: el fondo del
          // lienzo es lo que tapa el contenido que pasa por debajo, y con la franja
          // recortada al 90% ese contenido asomaría por los costados.
          //
          // Los 10px de aire de arriba pasaron de `mt-2.5` en la barra a `pt-2.5` acá:
          // un margen colapsa hacia afuera del wrapper y quedaría FUERA de la caja
          // pegada —o sea, contenido pasando por ese hueco—, y además es lo que hace
          // que `offsetHeight` mida la franja entera (ver la medición más arriba).
          <div
            ref={barraCreditos}
            className={`sticky top-0 z-30 pt-2.5 ${tema.fondo}`}
          >
          <div className="mx-auto max-w-[90%] px-8">
            <CreditsTopBar
              className="rounded-2xl"
              estado={estadoBarra}
              restantes={creditos}
              discountPercent={descuento}
              onDismiss={() => setBarraDescartada(true)}
              // El CTA abre el mismo overlay, con el copy del estado que la barra
              // acaba de anunciar — así el catálogo no contradice al cartel que lo
              // abrió (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo).
              onUpgrade={() => abrirPricing(estadoBarra)}
            />
          </div>
          </div>
        )}

        {/* Sólo ESTO se desvanece al cambiar de sección: la sidebar y la barra
            quedan quietas (ADR-2026-07-31-ruta-layout-shell-sidebar-deja). El `pb-6` (24px, ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo) es el aire de abajo
            para TODAS las secciones — las pantallas hospedadas traen `md:pb-0`
            porque contaban con el padding que ponía `.main-content`, que este
            shell no tiene, y sin esto terminan pegadas al borde.

            `max-w-[90%] mx-auto` es la columna de contenido de las TRES secciones
            (ADR-2026-07-31-columna-contenido-90-mejorar-plan): puesta acá y no en cada pantalla, la barra de créditos —que
            usa el mismo ancho— queda alineada con lo que hay debajo, sin que las
            pantallas hospedadas tengan que enterarse. El 5% de cada lado hace de
            padding lateral, así que la home ya no lleva el suyo. */}
        {/* Hospedaje NEUTRO para las pantallas mudadas desde `DashboardLayout` —
            ADR-2026-08-20-el-shell-hospeda-en-neutro-lo-que-no-nacio-para-la-columna.

            Esas 13 no se escribieron para este shell: contaban con el panel de
            `.main-content`, o sea ancho completo, alto definido y su propio padding.
            Metidas en el wrapper de abajo se les rompen tres cosas a la vez — el
            `padding: 0 !important` de `.shell-seccion` les come la sangría, el `h1`
            se les va a 32px, y —la peor— el `md:min-h-full` del Estudio de marketing
            deja de resolver, porque su contenedor pasaría a ser un div de alto `auto`
            en vez del panel.

            Acá NO se envuelve en NADA: el `<Outlet />` cuelga directo de `<main>`,
            que es un flex item con alto definido, igual que colgaba del panel fijo de
            `DashboardLayout`. Es el hospedaje que menos se parece a una decisión.

            Se pierde el fade de entrada en esas 13, y está bien: el fade es de las
            secciones nativas del shell, y un wrapper puesto sólo para animarlas es
            justamente el div de alto `auto` que rompe el alto. */}
        {hospedajeNeutro ? (
          <SidebarContext.Provider value={ctxShell}>
            <Outlet key={nonceMarca} />
          </SidebarContext.Provider>
        ) : (
        <div
          className={`shell-seccion mx-auto max-w-[90%] pb-6 transition-opacity ${
            visible
              ? 'opacity-100 duration-[680ms] ease-out'
              : 'opacity-0 duration-[240ms] ease-in'
          }`}
        >
          <SidebarContext.Provider value={ctxShell}>
            {/* La `key` es lo que remonta la pantalla al cambiar de marca
                (ADR-2026-08-03-cambiar-de-marca-remonta-la-pantalla). Va en el
                `Outlet` y NO en el `<div>` de arriba a propósito: ese div es el que
                hace el fade, y si se remontara nacería con `opacity-100` y la
                transición no tendría de dónde arrancar. */}
            <Outlet key={nonceMarca} />
          </SidebarContext.Provider>
        </div>
        )}
      </main>

      {/* Columna del chat de Atlas — ADR-2026-08-20-el-chat-viaja-con-las-pantallas-que-lo-abren.

          `PanelChatAtlas` vivía SÓLO en `DashboardLayout`. Las seis pantallas del
          Estudio de marketing tienen un botón "Pregúntale a Atlas"
          (`BotonPreguntaleAtlas`) que llama a `abrirChat()`, así que al mudarlas a este
          shell el botón habría prendido el estado global sin que se dibujara nada.

          Va sólo en hospedaje NEUTRO, o sea exactamente en las rutas que lo traían: en
          las secciones nativas del shell el panel hoy no existe y esta mudanza no es la
          ocasión para estrenarlo ahí. */}
      {hospedajeNeutro && (
        <SidebarContext.Provider value={ctxShell}>
          <ColumnaChatShell />
        </SidebarContext.Provider>
      )}

      {/* Catálogo de planes como overlay (ADR-2026-07-31-mejorar-plan-desde-abre-catalogo). Se portalea a `document.body`
          desde adentro del componente, así que da igual dónde se monte acá: no
          queda atrapado en el stacking context del shell. */}
      {motivoPricing && (
        <CreditsPricingSheet
          {...copyPricingPorMotivo(motivoPricing)}
          onClose={() => setMotivoPricing(null)}
        />
      )}
    </div>
    </CuentaContext.Provider>
  );
}

/**
 * Contenido de la home — ADR-2026-07-31-inicio-se-funde-interfaz-v2.
 *
 * **Es presentacional.** Nació como la pantalla de `/inicio`, que armaba su propio
 * contenido de catálogos estáticos. Ahora la monta `InterfazV2`, que es la home real
 * de la plataforma: la lista de modos, las colecciones de plantillas y qué pasa al
 * clickear cada cosa entran por props. Acá no queda ni una decisión de producto —
 * ni gating por plan, ni feature flags, ni navegación.
 *
 * Ese reparto es el punto: la lógica de la home vive en un solo lugar (`InterfazV2`,
 * con sus sheets, su restauración de scroll y su gating) y el diseño en otro. Antes
 * había DOS homes, una linda sin lógica y una con lógica y vieja.
 *
 * Lo único que sigue leyendo por su cuenta son las **creaciones recientes** (del
 * usuario, vía hook) y el estado de cuenta del encabezado (del shell, por contexto):
 * las dos son del usuario, no de la home, y `InterfazV2` no las conoce.
 *
 * Se separó del shell en ADR-2026-07-31-ruta-layout-shell-sidebar-deja para que las
 * secciones fueran rutas hijas de un layout común: así la sidebar no se desmonta al
 * navegar y el fade se aplica sólo al contenido.
 *
 * **Sólo desktop**: en mobile `InterfazV2` sigue con su `MobileModeHero` y sus
 * `CollectionRow`, bajo `DashboardLayout`.
 */
/**
 * Skeleton de la home — ADR-2026-08-03-home-aparece-entera-o-skeleton.
 *
 * Es el ESQUELETO DE ESTA home y no el `HomeSkeleton` de `HomeCards`: ese dibuja la
 * home vieja (saludo centrado, dos cards grandes) y usarlo acá cambiaría de layout a
 * mitad de la carga, que es justo lo que se está sacando.
 *
 * Las medidas están copiadas de los componentes reales —420px de la tira, la fila de
 * saludo, el alto del cartel— para que al terminar la carga nada se mueva de lugar.
 */
function HomeMinimalSkeleton() {
  const bloque = 'rounded-xl bg-black/[0.06] animate-pulse';
  // El esqueleto tiene que prometer lo que después se va a ver: si "Creaciones
  // recientes" no le corresponde a esta cuenta, su fila tampoco se dibuja acá — si no,
  // la carga muestra un hueco que desaparece y el resto de la home sube de golpe.
  //
  // Es el MISMO hook que usa la sección
  // (ADR-2026-08-20-creaciones-recientes-se-esconde-solo-para-moda) y no una copia de la
  // condición: acá había un `isPaidPlan(usePlanUsuario())` propio, o sea la regla escrita
  // dos veces. Con el corte pasando de "plan" a "plan + rubro", dos copias es la receta
  // para que el esqueleto y la sección discrepen — que es exactamente el parpadeo que
  // este `if` viene a evitar.
  const seVenCreaciones = useMuestraCreacionesRecientes();
  return (
    <div aria-hidden="true">
      {/* Saludo + accesos de cuenta: la misma fila de dos columnas.
          Las barras del saludo van DENTRO de un `<h1>`, y no es decorativo: los 40px
          de aire de arriba los pone una regla del shell que selecciona por
          `:has(> h1)` (`.shell-seccion`, ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo).
          Sin el `<h1>` la regla no matcheaba y el esqueleto arrancaba 40px más arriba
          que el contenido real. Copiar el `pt-10` a mano habría funcionado hasta que
          alguien cambie la regla; así los dos estados leen la misma fuente.
          Van `<span class="block">` y no `<div>` porque un `<h1>` espera contenido de
          frase. Los altos suman 77px, que es lo que mide el saludo real de dos
          renglones — medido, no calculado: la cuenta de `font-size × line-height` da
          69,6 y el navegador termina en 77. */}
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <h1 className="w-full max-w-[420px]">
          <span className={`block h-[38px] w-[62%] ${bloque}`} />
          <span className={`mt-2 block h-[31px] w-[48%] ${bloque}`} />
        </h1>
        <div className="flex shrink-0 items-center gap-2.5">
          <div className={`h-[34px] w-[150px] ${bloque}`} />
          <div className={`h-[34px] w-[112px] ${bloque}`} />
          <div className={`h-[34px] w-[34px] rounded-full bg-black/[0.06] animate-pulse`} />
        </div>
      </div>

      {/* Cartel. Va SIEMPRE, aunque después no corresponda ninguno: es el que tarda,
          y dejarle el lugar es la mitad del arreglo. Si no va, lo que pasa al
          terminar es que la tira sube — no que aparezca algo empujando. */}
      <div className={`mt-12 h-[92px] rounded-2xl bg-black/[0.06] animate-pulse`} />
      {/* Tira de modos: los mismos 420px de alto y el mismo gap. */}
      <div className="mt-12 flex h-[420px] gap-2.5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className={`${bloque} rounded-2xl`}
            // Los mismos `flexGrow` que `CardModo`: 2.8 la activa, 0.62 el resto.
            style={{ flexGrow: i === 0 ? 2.8 : 0.62, flexBasis: 0 }}
          />
        ))}
      </div>
      {/* Primera sección de abajo (creaciones recientes): título + fila de cards. */}
      {seVenCreaciones && (
        <div className="mt-12">
          <div className={`h-6 w-48 ${bloque}`} />
          <div className="mt-4 grid grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`aspect-[4/5] rounded-2xl bg-black/[0.06] animate-pulse`} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function HomeMinimal({
  variante = 'claro',
  modos,
  plantillas,
  totalPlantillas,
  banners,
  cargando = false,
  onVerTodasPlantillas,
}: {
  variante?: VarianteMinimal;
  /** Los modos de la tira, en orden, ya filtrados por quien corresponde. */
  modos: ModoHome[];
  /** Una fila por colección real. Vacío = no se dibuja la sección. */
  plantillas: FilaPlantillasHome[];
  /** Ver `SeccionesHome`: acá sólo pasa de largo. */
  totalPlantillas?: number;
  /**
   * Banners de home (describir la marca, encuesta, Tienda Nube, upsell). Entra como
   * nodo y no como estado porque quien decide cuál va —y con qué copy— es
   * `useHomeBanners` en `InterfazV2`; la home sólo le da un lugar en el layout.
   */
  banners?: ReactNode;
  /**
   * `true` mientras falte resolver algo de lo que se ve arriba del pliegue: el
   * cartel y el estado de cuenta del saludo. Mientras tanto va el esqueleto y la
   * home aparece entera de una vez — ADR-2026-08-03-home-aparece-entera-o-skeleton.
   */
  cargando?: boolean;
  onVerTodasPlantillas: () => void;
}) {
  const tema = TEMAS[variante];
  // Plan, créditos, identidad y el disparador del catálogo salen del shell: son los
  // mismos valores que muestra la sidebar y los que pisa el panel CONTEXTO DEV.
  const {
    // `abrirPricing` y `planAlDia` ya no se leen acá: la pastilla del encabezado
    // perdió su "Mejorar plan" y sólo informa el saldo
    // (ADR-2026-08-03-pastilla-creditos-solo-informa). Los dos siguen en el contexto
    // porque la sidebar los usa para SU "Mejorar plan" y para el gating de los modos.
    creditos,
    planLabel,
    nombreCompleto,
    idCompany,
    idMarca,
    cargandoCuenta,
    // El fade de salida lo presta el shell: la pastilla de créditos navega a
    // Configuración y tiene que hacerlo con la misma transición que la sidebar
    // (ADR-2026-08-04-pastilla-creditos-lleva-a-suscripcion).
    conFadeDeSalida,
  } = useContext(CuentaContext);

  // Modal del QR de soporte (ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio).
  // El estado vive acá, al lado del botón que lo abre, y no en el shell: es un detalle
  // de este encabezado, nadie más lo dispara.
  const [qrWhatsApp, setQRWhatsApp] = useState(false);
  const navigate = useNavigate();
  const nombre = nombrePila(nombreCompleto);
  // La home entra entera o no entra (ADR-2026-08-03-home-aparece-entera-o-skeleton).
  // Se espera al cartel Y al estado de cuenta: sin lo segundo el saludo aparecía como
  // "Buenas tardes" y se completaba con el nombre un instante después.
  if (cargando || cargandoCuenta) return <HomeMinimalSkeleton />;

  /* Acá se elegían 3 categorías al azar de `FILAS_PLANTILLAS`, el catálogo estático.
     Las filas ahora son las colecciones REALES que ya tenía cargadas `InterfazV2`
     (ADR-2026-07-31-inicio-se-funde-interfaz-v2), así que no hay nada que sortear:
     se muestran las que hay. */

  // Sin ancho ni padding propios: la columna la define el shell
  // (`max-w-[90%] mx-auto`, ADR-2026-07-31-columna-contenido-90-mejorar-plan) para que las tres secciones y la barra de
  // créditos compartan exactamente el mismo ancho.
  return (
        // `aparece-tras-esqueleto`: el contenido no reemplaza de golpe a los bloques
        // grises, entra con una opacidad y 6px de subida
        // (ADR-2026-08-03-home-aparece-entera-o-skeleton). La animación corre una vez
        // por montaje —y el cambio esqueleto → contenido ES un montaje, porque React
        // ve dos tipos distintos en la misma posición—, así que no se repite en cada
        // re-render.
        <div className="aparece-tras-esqueleto">
          {/* Bienvenida y accesos de cuenta comparten fila, centrados entre sí. El
              `pt-10` deja la mitad del aire que había con la barra superior aparte. */}
          {/* Sin `pt-10`: los 40px de arriba del título los pone ahora la regla
              compartida del shell, para que las tres secciones abran igual
              (ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo). */}
          {/* En pantallas angostas la fila se apila: el título y los tres controles
              de cuenta no entran juntos por debajo de ~640px, y en `md:` para arriba
              —que es donde vive el shell nuevo— sigue siendo exactamente la fila de
              antes. Ver ADR-2026-07-31-shell-nuevo-urls-viejas. */}
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <h1 className="text-[32px] font-bold leading-[1.2] tracking-[-0.02em]">
              {/* Sin nombre todavía, el saludo va solo: "Buenas tardes," con una coma
                  colgando es peor que "Buenas tardes". Ver `nombrePila`. */}
              {saludo()}
              {nombre && `, ${nombre}`}
              <br />
              {/* `font-[600]` y no `font-semibold`: el config del proyecto mapea
                  `semibold` a 530, no a 600. */}
              <span className="text-[26px] font-[600] text-black/40">
                ¿Qué quisieras crear hoy?
              </span>
            </h1>

            {/* Accesos de cuenta — ADR-2026-07-31-creditos-mejora-plan-encabezado-home, supersede ADR-2026-07-31-encabezado-mejorar-plan-centro-ayuda en el CTA de mejora.
                Réplica de la referencia: ayuda con etiqueta, los créditos y la mejora
                juntos en una pastilla, y el avatar al final. Los créditos y el CTA van
                pegados porque el número es lo que le da sentido a la oferta; sueltos,
                "Mejorar plan" es un botón más. */}
            <div className="flex shrink-0 items-center gap-2.5 text-[13px]">
              {/* Con etiqueta y encerrado: como texto suelto sobre el fondo no se
                  leía como un control. La superficie y el borde son los de las
                  tarjetas del resto de la pantalla, así que acompaña a la variante. */}
              {/* `h-[34px]` acá, en la pastilla de créditos y en el avatar: los tres
                  controles miden lo mismo. Con paddings verticales cada uno terminaba
                  con un alto distinto según su contenido (ícono de 18 vs texto de
                  12,5), y en una fila de tres se nota. */}
              {/* Dos lecturas del mismo bloque según el estado de la cuenta
                  (ADR-2026-07-31-pastilla-encabezado-tiene-dos-lecturas). Con algo que resolver es una OFERTA: saldo + botón sólido
                  "Mejorar plan". Sin nada que resolver es un DATO: la pastilla entera
                  informa el saldo disponible, y como ya no compite con nada el número
                  y su etiqueta se juntan. */}
              {/* La pastilla tiene UNA sola forma —el saldo, sin CTA adentro—
                  (supersede ADR-2026-07-31-pastilla-encabezado-tiene-dos-lecturas y
                  ADR-2026-08-03-pastilla-creditos-no-cambia-con-el-saldo) y desde
                  ADR-2026-08-04-pastilla-creditos-lleva-a-suscripcion vuelve a ser
                  clickeable: lleva a Configuración → Suscripción, que es donde el
                  número que muestra está explicado (plan, consumo del período,
                  próxima renovación). No abre el catálogo: la pastilla informa un
                  saldo, no ofrece un upgrade — eso es "Mejorar plan", en la sidebar. */}
              <button
                type="button"
                title={`${planLabel} — ver tu suscripción`}
                onClick={() =>
                  conFadeDeSalida(() =>
                    navigate('/configuracion', { state: { section: 'plan' } }),
                  )
                }
                className="flex h-[34px] items-center gap-1.5 rounded-xl bg-[#EBEFFE] px-3.5 text-[12.5px] font-semibold text-brand-primary ring-1 ring-[rgba(22,68,235,0.22)] transition-colors hover:bg-[#DFE7FD] hover:ring-[rgba(22,68,235,0.34)]"
              >
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="h-3.5 w-3.5"
                />
                <span className="tabular-nums text-[13px]">{creditos}</span>
                {/* -ml-0.5 achica 2px sólo el espacio número↔etiqueta, sin
                    tocar el del ícono↔número (el gap del contenedor es común). */}
                <span className="-ml-0.5">
                  {creditos === 1 ? 'Crédito' : 'Créditos'}
                </span>
              </button>

              {/* En una compu abre el modal del QR; en un teléfono, WhatsApp directo —
                  ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio.

                  `wa.me` en desktop abre WhatsApp Web, que mucha gente no tiene
                  logueada: el link llevaba a una pantalla de QR ajena en vez de al
                  chat. Pero en un celular el link directo ES lo correcto, y mostrarle
                  "escaneá el QR con tu celular" a alguien que YA está en el celular es
                  absurdo.

                  El corte va en `md` (768) y NO en `sm`, con las mismas dos ramas por
                  CSS que usa `ContactarPorWhatsApp` (`md:hidden` / `hidden md:inline-flex`).
                  Este encabezado no es desktop-only como parece: `InterfazV2` lo monta
                  con `hidden sm:block`, o sea desde 640, mientras que el shell de
                  escritorio arranca en 768 (ADR-2026-08-05-el-shell-nuevo-arranca-en-768).
                  En esa franja de 640 a 767 —un iPhone en horizontal— la app es MOBILE
                  y el encabezado igual se dibuja. Con un solo `<button>` ahí salía el QR.

                  Las clases son EXACTAMENTE las que tenía el `<a>` original: el stroke
                  va como `ring` y no como `border` —dibujado por FUERA de la caja, no le
                  come 1px de alto ni de padding al control—. Por eso se consume
                  `ModalQRWhatsApp` suelto y no `ContactarPorWhatsApp`, que trae su
                  propio botón azul de 40px. */}
              <a
                href={urlsWhatsApp({ mensaje: MENSAJE_SOPORTE }).directa}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex h-[34px] items-center gap-1.5 rounded-xl px-3.5 font-[500] ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] md:hidden ${GRIS_SHELL} ${tema.tarjeta}`}
              >
                <Icon name="solid/whatsapp" size={18} className="shrink-0" />
                Whatsapp
              </a>
              <button
                type="button"
                onClick={() => setQRWhatsApp(true)}
                className={`hidden h-[34px] items-center gap-1.5 rounded-xl px-3.5 font-[500] ring-1 ring-black/[0.08] transition-colors hover:text-black/85 hover:ring-black/[0.14] md:flex ${GRIS_SHELL} ${tema.tarjeta}`}
              >
                {/* Logo de WhatsApp (sólido) y no el ícono genérico de soporte: acá
                    la etiqueta ya NO es "centro de ayuda" sino el canal concreto, y
                    el logo es lo que hace obvio a dónde lleva. El centro de ayuda
                    propiamente dicho vive en la sidebar y en el menú de cuenta. */}
                <Icon name="solid/whatsapp" size={18} className="shrink-0" />
                Whatsapp
              </button>
              <ModalQRWhatsApp
                abierto={qrWhatsApp}
                onCerrar={() => setQRWhatsApp(false)}
                mensaje={MENSAJE_SOPORTE}
                titulo="Hablá con soporte"
              />

              {/* El avatar abre el menú de cuenta (ADR-2026-07-31-menu-cuenta-avatar-uno-solo), el mismo que el de la
                  sidebar. Sólo acá lleva aro azul: es el único de los cuatro que vive
                  sobre el fondo pelado, sin tarjeta ni panel que lo recorte, y sin él
                  se pierde contra el gris. Va como `ring` y no como `border` para no
                  achicar el círculo ni correr la inicial. La pastilla de créditos usa
                  ese mismo aro (ADR-2026-07-31-pastilla-creditos-toma-aro-avatar).

                  1 px, el mismo grosor que el borde de "Centro de ayuda", y al 22% —
                  suficiente para separarlo del fondo sin leerse como un estado
                  seleccionado.

                  El azul va escrito como `rgba` y no como `ring-brand-primary/22`:
                  `brand.primary` es `var(--brand-primary)` sin `<alpha-value>`, así que
                  Tailwind no le puede aplicar el modificador de opacidad y el aro
                  quedaba del azul por defecto del framework (ADR-2026-07-31-encabezado-cta-comparte-criterio-sidebar). */}
              <MenuCuenta variante="home">
                <AvatarUsuario
                  px={34}
                  className="!bg-white transition-shadow hover:ring-black/[0.14]"
                />
              </MenuCuenta>
            </div>
          </div>

          {/* Banners de home (describir la marca, encuesta, Tienda Nube, upsell) —
              ADR-2026-07-31-inicio-se-funde-interfaz-v2. Van entre el saludo y la
              tira: son avisos sobre la cuenta, así que tienen que verse sin scroll,
              pero no pueden empujar el contenido de la home más abajo del pliegue.

              En el diseño viejo de `/interfaz-v2` iban al costado del saludo
              (ADR-2026-07-31-banners-desktop-solo-upsell-cede). Acá no entran ahí: esa
              fila la ocupan los tres controles de cuenta. */}
          {banners}

          {/* Tira de modos. Cuáles son y qué hace cada uno lo decide el llamador; de
              acá sale sólo la carátula (ver `CarruselModos`). */}
          <CarruselModos modos={modos} tema={tema} />

          {/* De acá para abajo, desktop y mobile muestran lo MISMO
              (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar). */}
          <SeccionesHome
            idCompany={idCompany}
            idMarca={idMarca}
            plantillas={plantillas}
            totalPlantillas={totalPlantillas}
            onVerTodasPlantillas={onVerTodasPlantillas}
            variante={variante}
          />
        </div>
  );
}

/**
 * Cierre de la sección Plantillas — el acceso al catálogo completo al PIE de la
 * sección, no sólo en el "Ver todo" del encabezado.
 *
 * La home muestra 3 de las ~10 categorías (ADR-2026-07-31-plantillas-home-rotan-tres) y
 * hasta acá lo único que llevaba al catálogo entero era el "Ver todo" chico del
 * título: a la altura donde el usuario TERMINA de mirar las filas —tres tarjetas más
 * abajo— no quedaba ningún camino, y la sección se leía como si eso fuera todo el
 * catálogo. El pie de una lista parcial es justo donde aparece la pregunta que este
 * bloque contesta.
 *
 * **Es la anatomía de los carteles de home** (`HomeBanners`, kind "cuestionario"):
 * tarjeta blanca con borde, ícono de marca + título en una línea, y CTA suave
 * (lavanda con texto azul) a la derecha, reducido a la flecha en mobile. Es lo que
 * este bloque ES —una sugerencia al pie, no algo que haya que resolver—, y la home ya
 * tiene ese componente arriba: dos bloques con la misma anatomía y dos estilos
 * distintos se leen como dos sistemas.
 *
 * Toda la tarjeta es el botón (la pastilla es decorativa, con `group-hover`): con
 * texto clickeable + botón al lado, el 80% del área no hacía nada. Los carteles de
 * `HomeBanners` no pueden hacer esto porque ahí la tarjeta lleva además el descarte.
 *
 * El número sale del catálogo cargado, no de una constante que se desactualiza. Sin
 * él, el copy cae en una versión sin números que sigue siendo verdadera.
 */
function CierrePlantillas({
  totalPlantillas,
  onVerTodas,
}: {
  /** Cuántas plantillas tiene el catálogo completo. Sin esto, el copy va sin números. */
  totalPlantillas?: number;
  onVerTodas: () => void;
}) {
  // El número va REDONDEADO HACIA ABAJO a la decena y con un "+" adelante: "+180" se
  // lee como un tamaño —que es lo que el título quiere transmitir— mientras que "183"
  // se lee como un inventario exacto, y encima envejece con cada plantilla que se
  // suma o se da de baja. Hacia abajo y nunca hacia arriba: el "+" tiene que ser
  // verdadero para cualquier catálogo, no sólo para el de hoy.
  //
  // Por debajo de 20 no hay redondeo que se lea como "muchas" (un "+10" con 19
  // plantillas suena a menos de lo que hay), así que ahí el título va sin número.
  const redondeado =
    totalPlantillas && totalPlantillas >= 20
      ? Math.floor(totalPlantillas / 10) * 10
      : 0;
  const titulo = redondeado
    ? `Mirá las de +${redondeado} plantillas disponibles`
    : 'Mirá todas las plantillas disponibles';
  // La versión corta de mobile: sin el "Mirá las" —a 390px el CTA queda reducido a la
  // flecha y el título tiene que entrar en una línea al lado del ícono—, pero con
  // "disponibles", que es lo que lo hace una frase y no una etiqueta.
  const tituloCorto = redondeado
    ? `+${redondeado} plantillas disponibles`
    : 'Todas las plantillas disponibles';

  return (
    <button
      type="button"
      onClick={onVerTodas}
      // Mismas clases que la tarjeta de los carteles de `HomeBanners`. El literal se
      // repite en vez de importarse porque aquel vive en un componente que esta
      // pantalla no consume, igual que ellos repiten los colores del CTA suave.
      className="group mt-4 relative flex w-full items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4 text-left"
    >
      <span className="flex min-w-0 items-center gap-2">
        <LayoutGrid className="h-4 w-4 shrink-0 text-brand-primary" />
        {/* `truncate`: el número puede crecer y el título no puede empujar al CTA
            fuera de la tarjeta. */}
        <span className="truncate text-[14px] font-semibold text-brand-black/90 sm:text-[15px]">
          <span className="sm:hidden">{tituloCorto}</span>
          <span className="hidden sm:inline">{titulo}</span>
        </span>
      </span>
      {/* CTA suave (lavanda + azul de marca) y cuadrado de 36px en mobile, donde queda
          sólo la flecha — ADR-2026-08-04-cta-solo-icono-es-cuadrado. El hover lo
          dispara la tarjeta entera, que es el botón real. */}
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#EBEFFE] text-[14px] font-medium text-brand-primary transition-colors group-hover:bg-[#DFE6FD] sm:h-10 sm:w-auto sm:px-4">
        <span className="hidden sm:inline">Ver todas</span>
        <ChevronRight className="h-4 w-4" />
      </span>
    </button>
  );
}

/**
 * Creaciones recientes + Plantillas + Ediciones rápidas — el cuerpo de la home,
 * COMPARTIDO por las dos versiones (ADR-2026-07-31-home-mobile-hero-nav-sheet-generar).
 *
 * Lo que cambia entre desktop y mobile es lo de ARRIBA: desktop abre con el saludo,
 * los accesos de cuenta y la tira de modos; mobile abre con el hero a sangre y su
 * nav inferior. De acá para abajo las dos muestran lo mismo, y por eso se extrajo
 * en vez de duplicarse: las grillas ya traían sus breakpoints (`grid-cols-2` en
 * teléfono), así que no hubo que adaptar nada.
 *
 * Sigue siendo PRESENTACIONAL, igual que `HomeMinimal`: las plantillas y sus
 * handlers entran por props desde `InterfazV2`. Lo único que lee por su cuenta son
 * las creaciones recientes, que son del usuario y no de la home.
 *
 * Recibe `variante` y no `tema`: `TemaMinimal` es interno de este módulo, así el
 * consumidor de afuera no necesita construir un objeto que no puede nombrar.
 */
export function SeccionesHome({
  idCompany,
  idMarca,
  plantillas,
  totalPlantillas,
  onVerTodasPlantillas,
  variante = 'claro',
}: {
  idCompany: string | null;
  /** Marca activa — la usa "Creaciones recientes" para mostrar sólo lo suyo
   *  (ADR-2026-08-03-creaciones-recientes-por-marca). */
  idMarca: string | null;
  plantillas: FilaPlantillasHome[];
  /** Cuántas plantillas tiene el catálogo completo — el cierre de la sección lo dice
   *  en su título; la home sólo recibe las 3 categorías que rotan y desde ahí ese
   *  total es invisible. */
  totalPlantillas?: number;
  onVerTodasPlantillas: () => void;
  variante?: VarianteMinimal;
}) {
  const navigate = useNavigate();
  const tema = TEMAS[variante];

  /* Plan para el candado de los atajos — ADR-2026-08-20-el-plan-de-una-seccion-no-puede-venir-del-shell.

     NO sale de `CuentaContext`, aunque el contexto esté ahí y sea lo cómodo. Este
     componente tiene DOS puntos de montaje: dentro de `HomeMinimal` (bajo el provider) y
     directo desde `InterfazV2`, en su rama `sm:hidden` de mobile. En un teléfono el shell
     es `ShellMobile` y `EstiloMinimal` —el único provider de la app— NO se monta, así que
     ese segundo montaje corría con el default del contexto: `plan: ''`, que
     `getModePlanLock` interpreta como "todavía no sé" y no bloquea nada. Resultado: el
     candado existía en desktop y no en mobile, que es justo donde está la mayoría de las
     cuentas gratis.

     Se lee con el patrón canónico del repo (`Modos.tsx`, `SheetModosProducto`,
     `useModosGenerar`): cache de localStorage para el primer paint, valor real después.
     Funciona igual en los dos montajes, sin depender de quién sea el padre. */
  const [planCuenta, setPlanCuenta] = useState<string | null>(() => {
    try {
      return localStorage.getItem('atlas-user-plan');
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelado = false;
    getUserPlan().then((p) => {
      if (!cancelado && p) setPlanCuenta(p);
    });
    return () => {
      cancelado = true;
    };
  }, []);

  /* A dónde va el upsell. `dentroDelShell` ya existe en el contexto para exactamente
     esto, y su default es `false`, que es lo correcto fuera del shell: `abrirPricing`
     es un no-op ahí, así que usarlo a ciegas convertía el atajo bloqueado en un click
     muerto. Misma bifurcación que ya hace `SheetModosProducto`. */
  const { abrirPricing: abrirPricingCuenta, dentroDelShell } =
    useContext(CuentaContext);
  const abrirPlanes = () =>
    dentroDelShell ? abrirPricingCuenta('bloqueado') : navigate('/subscription');

  return (
    <>
          {/* Creaciones recientes — una sola fila: es un vistazo, no el historial.
              Para verlo entero está "Ver todo". Contenido REAL desde
              ADR-2026-07-31-shell-nuevo-datos-reales; antes eran las imágenes de demo de `contenidoAtlas`. */}
          <CreacionesRecientes idCompany={idCompany} idMarca={idMarca} tema={tema} />

          {/* Plantillas — una fila por colección REAL (ADR-2026-07-31-inicio-se-funde-interfaz-v2).
              Antes eran 3 categorías sorteadas de un catálogo estático; ahora son las
              colecciones que `InterfazV2` ya tenía cargadas, y clickear una abre su
              sheet en vez de mandar al listado. */}
          {plantillas.length > 0 && (
            <section className="mt-12">
              {/* Igual que "Creaciones recientes": pegado al título y apoyado en su pie. */}
              <div className="flex items-end gap-3">
                <div>
                  {/* En mobile, sólo "Plantillas": la frase entera ocupa dos
                      renglones de los tres que mide la tarjeta antes de la primera
                      miniatura (ADR-2026-08-04-titulo-de-plantillas-corto-en-mobile).
                      El título de la sección no tiene que explicar la sección: eso lo
                      hace la bajada de cada fila. */}
                  <h2 className="text-[22px] font-bold tracking-[-0.01em]">
                    <span className="sm:hidden">Plantillas</span>
                    <span className="hidden sm:inline">
                      Plantillas con escenas ya armadas
                    </span>
                  </h2>
                </div>
                <button onClick={onVerTodasPlantillas} className={BOTON_VER_TODO}>
                  Ver todo <MoveRight className="h-4 w-4" strokeWidth={2.25} />
                </button>
              </div>

              {/* Cada colección en su propia tarjeta, como las de "Ediciones rápidas":
                  así se lee como un bloque cerrado y no como grillas sueltas. */}
              {plantillas.map((fila) => (
                <div
                  key={fila.id}
                  // `overflow-hidden`: la fila de adentro va a sangre hasta el borde
                  // de esta tarjeta, y sin esto el scroll horizontal pintaba las
                  // miniaturas encima del borde y de las esquinas redondeadas
                  // (ADR-2026-08-04-la-fila-se-recorta-en-la-tarjeta).
                  className={`mt-4 overflow-hidden rounded-2xl border border-black/[0.06] p-4 ${tema.tarjeta}`}
                >
                  <div className="mb-3.5">
                    <h3 className="text-[16.5px] font-semibold tracking-[-0.01em]">
                      {fila.titulo}
                    </h3>
                    {/* `hidden sm:block`: en mobile la descripción no entra en una
                        línea y parte la tarjeta en dos renglones de texto antes de
                        que se vea una sola miniatura. El título alcanza para saber
                        qué es la fila; la descripción es contexto de desktop
                        (ADR-2026-08-03-descripciones-plantillas-100-y-fuera-en-mobile). */}
                    {fila.bajada && (
                      <p className="mt-0.5 hidden text-[12.5px] text-black/45 sm:block">
                        {fila.bajada}
                      </p>
                    )}
                  </div>

                  {/* 5 plantillas + "Ver más" como sexta celda: el acceso a la lista
                      completa vive en la grilla, a la misma altura que lo que muestra.
                      La celda "Ver más" sólo aparece si hay MÁS de 5: con 3 items,
                      prometía un resto que no existe. */}
                  <div className={FILA_MINIATURAS}>
                    {fila.items.slice(0, 5).map((it) => (
                      <button
                        key={it.id}
                        onClick={it.onAbrir}
                        className={`group relative aspect-[3/4] overflow-hidden rounded-xl border border-black/[0.06] ${CELDA_MINIATURA} ${tema.hueco}`}
                      >
                        <img
                          src={it.src}
                          alt={it.titulo}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                        />
                        <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent px-2.5 pb-2 pt-8 text-left">
                          <span className="block truncate text-[12.5px] font-medium text-white">
                            {it.titulo}
                          </span>
                        </span>
                      </button>
                    ))}

                    {fila.items.length > 5 && (
                      <CeldaVerMas
                        etiqueta={`Ver todas las plantillas de ${fila.titulo}`}
                        onClick={fila.onVerMas}
                        tema={tema}
                      />
                    )}
                  </div>
                </div>
              ))}

              {/* Cierre de la sección: el "Ver todo" del título queda tres tarjetas
                  arriba, y acá es donde el usuario termina de mirar. */}
              <CierrePlantillas
                totalPlantillas={totalPlantillas}
                onVerTodas={onVerTodasPlantillas}
              />
            </section>
          )}

          {/* Ediciones rápidas — cierra la home: son acciones SOBRE lo ya generado,
              así que van después de las creaciones, no antes. */}
          <section className="mt-12">
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">Ediciones rápidas</h2>

            {/* En MOBILE, cuatro por fila y sólo ícono + nombre
                (ADR-2026-07-31-home-mobile-filas-horizontales-ediciones-compactas): la tarjeta completa —con su
                descripción y 28px de padding vertical— apilaba SIETE bloques a lo
                largo de la pantalla para cerrar la home. La descripción se
                esconde, no se borra: en desktop hay lugar y es donde se entiende
                qué hace cada acción. */}
            <div className="mt-5 grid grid-cols-4 gap-2 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
              {EDICIONES_RAPIDAS.map((edicion) => {
                const { label, desc, Icono, ruta } = edicion;
                // Candado por plan — ADR-2026-08-20-los-atajos-de-carrusel-y-animar-van-a-su-modo.
                // Hoy sólo lo tiene Animar. Bloqueado NO navega: abre el catálogo de
                // planes, el mismo gesto que la card del modo en la tira y que su ítem
                // en la sidebar.
                const lock = candadoEdicionRapida(edicion, planCuenta);
                return (
                <button
                  key={label}
                  onClick={() => (lock ? abrirPlanes() : navigate(ruta))}
                  title={lock ? lock.title : undefined}
                  className={`group flex flex-col items-center justify-start rounded-2xl border border-black/[0.06] px-2 py-3 text-center transition-all sm:px-5 sm:py-7 sm:hover:-translate-y-0.5 sm:hover:border-black/[0.1] sm:hover:shadow-[0_8px_28px_rgba(16,24,45,0.07)] ${tema.tarjeta}`}
                >
                  <span
                    className={`mb-1.5 grid h-9 w-9 place-items-center rounded-xl text-[#16181D]/75 transition-colors group-hover:bg-[#EBEDF7] group-hover:text-brand-primary sm:mb-3.5 sm:h-11 sm:w-11 sm:rounded-2xl ${tema.hueco}`}
                  >
                    {lock ? (
                      <span className="material-symbols-rounded text-[18px] sm:text-[21px]">
                        lock
                      </span>
                    ) : (
                      <Icono className="h-4 w-4 sm:h-[19px] sm:w-[19px]" strokeWidth={1.8} />
                    )}
                  </span>
                  <span className="text-[11.5px] font-medium leading-tight sm:text-[14.5px] sm:font-semibold">
                    {label}
                  </span>
                  <span className="mt-1 hidden text-[13px] leading-relaxed text-black/45 sm:block">
                    {desc}
                  </span>
                </button>
                );
              })}
            </div>
          </section>
    </>
  );
}
